import { DC } from "../constants";

import { credits } from "@/core/secret-formula/credits";

export const h2p = {
  /**
   * @template
   * {
   *  @property {String} name   Internal name for the tab entry
   *  @property {String} alias  Display name for the tab; if not present, will use the internal name
   *  @property {Number} id     Unique ID for each entry (generated in-game, not explicitly stated)
   *  @property {function: @return String} info         Text body of information for the entry
   *  @property {function: @return Boolean} isUnlocked  Condition for when the entry is visible and searchable
   *  @property {Array: String} tags  List of keywords which are linked to this tab in the search function
   *  @property {String} tab    Key of a tab+subtab combination which will default the h2p to this entry if opened
   * }
   */
  tabs: [
    {
      name: "This Modal",
      textName: "Это Меню",
      info: () => `
Добро пожаловать в раздел Как Играть!
<br>
<br>
Это меню (всплывающее окно) содержит подробные объяснения и дополнительную информацию обо всём, что вы встретите
по ходу прогресса в игре. По мере разблокировки механик вы также будете получать доступ к дополнительным
страницам в этом меню. Если вы когда-нибудь почувствуете что застряли или не понимаете как работает игра - вы можете
найти полезные разъяснения внутри сответствующей вкладки в этом меню.
<br>
<br>
В начале игры это меню всегда открывается на этой странице. После того как вы получите свой первый Буст
Измерений, открытие этого меню сразу же перенесёт в вас на наиболее подходящую для текущей игры вкладку.
`,
      isUnlocked: () => true,
      tags: ["h2p", "how", "to", "play", "modal"],
      tab: ""
    },
    {
      name: "Your savefile",
      textName: "Ваш файл сохранения",
      info: () => `
Ваше сохранение хранится в данных вашего браузера. Это означает, что очистка кэша или куки в вашем браузере, 
в случае, если вы не авторизованы, удалит ваше сохранение.
Похожим образом, если вы играете в приватном окне или в режиме инкогнито и не авторизованы, ваше сохранение
удалится после закрытия браузера. Сохранения также не переносятся между браузерами.
<br>
<br>
Вы можете перемещать ваше сохранение с помощью функции экспорта, которая скопирует <i>очень</i>
длинную строку символов в ваш буфер обмена. Этот текст содержит данные вашего сохранения. Вы можете загрузить его
в игру с помощью кнопки 'Импортировать Сохранение'. Текст должен быть вставлен целиком, иначе он не будет распознан.
Некоторые мессенджеры и социальные сети (практически все) могут обрезать часть текста из-за ограничения символов,
поэтому будьте аккуратны.
<br>
<br>
Правильно отформатированный текст начинается с
<b>${GameSaveSerializer.startingString.savefile}</b> и заканчивается <b>${GameSaveSerializer.endingString.savefile}</b>.
Если что-то из этого отсутствует - часть вашего сохранения потерялась и вы не сможете импортировать данное
сохранение. В дополнение к импорту и экспорту в буфер обмена, вы также можете экспортировать/импортировать
сохранение в/из текстовый файл/а.
<br>
Вы можете использовать кнопку "Выбрать сохранение", чтобы перемещаться между тремя слотами сохранений. Эти сохранения
полностью отделены друг от друга. Импорт и экспорт повлияет только на текущий слот сохранений.
<br>
<br>
Игра переодически автоматически сохраняется. По стандарту это происходит каждые ${formatInt(30)} секунд.
Держите это в уме, если хотите закрыть игру - всё, что вы делаете до закрытия может не быть сохранено до тех пор, пока
не произойдёт автосохранение или вы не сохранитесь вручную. Интервала автосохранения можно изменить.
Таймер можно увидеть в левом нижнем углу экрана.
<br>
<br>
Существуют также бэкапы ваших сохранений, которые сохраняются после определённого количества времени офлайн или онлайн.
Вы можете посмотреть их и загрузить в любой момент, нажав на кнопку "Открыть Меню Автоматических Резервных
Сохранений". Эти бэкапы могут быть полезны, если вы хотите откатить ваш файл сохранений.
<br>
<br>
Если вы авторизованы, игра подключит ваши сохранения к облаку. Если нет - вы можете
сделать это, для того чтобы надёжно сохранить ваш прогресс. Это позволит вам играть на абсолютно разных устройствах
и не бояться потери данных.
<br>
<br>
Вы можете полностью сбросить ваше сохранение в любой момент, нажав на кнопку 'СБРОСИТЬ ИГРУ'. Она откроет меню,
которое попросит вас ввести определённую фразу, чтобы удостовериться, что вы хотите совершить полный сброс. Продолжение
этого сброса сбросит только текущий слот сохранения. Другие слоты не будут изменены. <b>Сброс игры таким образом
полностью необратим и не даёт вам никаких бонусов, секретов или чего бы то ни было ещё.</b>
`,
      isUnlocked: () => true,
      tags: ["choose", "cloud", "google", "save", "import", "export", "reset"],
      tab: "options/saving"
    },
    {
      name: "Customization",
      textName: "Кастомизация",
      info: () => `
Игра имеет два различных интерфейса - Классический интерфейс и Современный.
Дополнительно, существуют различные темы, которые могут быть применены вручную для изменения
внешнего вида игры. Существует несколько секретных тем, которые могут быть разблокированы с помощью импорта
определённых фраз в поле импорта сохранений. Попробуйте, например, импортировать фразу Confused.
<br>
<br>
Форма записи используется для отображения чисел в игре. Стандартной является Смешанная Научная, но она может быть
изменена. Большое количество форм записи созданы в качестве шутки
и будут показывать числа таким образом, что они будут вылезать за элементы интерфейса или экран - это не баг. Вы
дополнительно можете настроить то, как числа выглядят когда они экстремально большие, используя меню "Настройки
Экспоненциальной Записи", однако это тоже может привести к поломке интерфейса в некоторых местах.
<br>
<br>
Множество событий в игре используют полноэкранные анимации или всплывающие окна, требующие от вас подтверждения
определённого действия для продолжения. Эти анимации и подтверждения могут быть индивидуально отключены
в настройках. Возможность отключить определённую анимацию или подтверждение появится только тогда, когда вы
увидите то, что хотите отключить, хотя бы раз.
`,
      isUnlocked: () => true,
      tags: ["UI", "update", "news", "theme", "notation", "comma", "exponent", "animation", "retry", "confirmation",
        "offline", "hotkey", "classic", "modern"],
      tab: "options/visual"
    },
    {
      name: "Offline Progress",
      textName: "Офлайн Прогресс",
      info: () => `
Измерения Антиматерии имеют механику навёрстывания, симулирующую поведение игры так, как если бы она не была
закрыта. Эта симуляция не полностью аккуратна.
<br>
<br>
Если игра оставлена открытой, но теряет фокус или ещё как-либо отложена на длительный промежуток времени, она попытается
применить пропущенное время как офлайн прогресс, когда вы вернётесь. Если это приводит к нежелаемому поведению - в
настройках есть переключатель для данного поведения.
<br>
<br>
Игра работает в системе, где всё обновляется каждый тик - все Измерения и русурсы создают одну единицу продукции,
все АвтоПокупщики срабатывают единажды, все множители и значения изменяются соответственно, и все отображаемые числа
обновляются. По стандарту игра работает на 20 тиках в секунду, когда запущена, хотя это может быть изменено с
помощью кнопки "Частота Обновления".
Ваши текущие настройки запускают игру на ${format(1000 / player.options.updateRate, 2, 1)} тиках в секунду в
среднем, хотя тормоза могут приводить к отличиям в тиках на пару процентов.
<br>
<br>
Когда офлайн симуляция активна, эти тики имеют отрегулированную длину для того чтобы заполнить количество времени,
которое вы отсутствовали - например, имея настройку на ${formatInt(1000)} офлайн тиков, закрытие игры на час приведёт к
тикам длиной ${format(3.6, 1, 1)} секунд каждый. Для большинства механик в игре это не проблема, однако
на некоторые механики это может значительно повлиять. Например, АвтоПокупщики.
В данном примере они будут срабатывать только каждые ${format(3.6, 1, 1)} секунд, что может иметь серьёзное
влияние на прогресс.
<br>
<br>
${player.blackHole[0].unlocked
    ? `<b>Поведение Чёрных Дыр в офлайне:</b> как только чёрная дыра разблокируется, симуляция офлайн прогресса будет
      пытаться запустить игру таким образом, что каждый тик будет содержать примерно такое же <i>игровое</i> время. Вам
      может показаться, что Чёрные Дыры активны большее времени, чем обычно, но на самом деле
      игра делает активные периоды более медленными и пропускает неактивные периоды, потому что они вносят гораздо
      меньше производства за реальное время. Эти результаты могут привести к поведению в вашу сторону по сравнению
      с тиками с постоянным реальным временем.
      <br>
      <br>`
    : ""
}
Количество офлайн тиков может быть изменено с ${formatInt(500)} до ${formatInt(DC.E6)} тиков. Меньшее количество
приведёт к более быстрому просчёту, но менее точным результатам, в то время как большее количество приведёт к более
точному результату, но займёт больше времени. Существует лимит в один день за тик.
<br>
<br>
Офлайн прогресс может быть полностью отключен если вы захотите.
Заметьте, что если офлайн прогресс отключен, статистика общего времени игры также будет
останавливаться после закрытия игры.
`,
      isUnlocked: () => true,
      tags: ["offline", "away", "progress"],
      tab: "options/gameplay"
    }, {
      name: "Effect Stacking",
      textName: "Наложение Эффектов",
      info: () => `
Большинство эффектов и улучшений в игре попадают под три категории:
<br>
- <b>Аддитивные:</b> Эти эффекты обычно обозначены знаком + (или словом "повышает"), а затем числом. Они добавляют
своё значение к некоторому базовому значению. Аддитивные эффекты суммируются.
<br>
- <b>Мультипликативные:</b> Эти эффекты обозначаются знаком × (или словом "умножить"), а затем числом, или,
более редко, двумя числами, разделённымиы ➜. Различные мультипликативные эффекты умножаются друг на друга и
никогда не складываются.
<br>
- <b>Степень</b>: Эти эффекты гораздо более редкие и обозначаются знаком ^ и числом. Эффекты стпени
применяются последовательно. Эффект степени иногда называется мощностью.
<br>
<br>
Если не указано обратное, все эти эффекты
состыкуются друг с другом. В случае улучшения, заменяющего более старое значение новым, замена происходит до того,
как все вышеперечисленные эффекты применены. Чтобы определить последнее значение набора эффектов, эффекты от каждой
категории индивидуально совмещаются, а затем применяются в порядке: аддитивные, мультипликативные, степень.
<br>
<br>
${PlayerProgress.realityUnlocked() || PlayerProgress.dilationUnlocked()
    ? "Эффекты Растяжения и похожие на Растяжение применяются <i>после</i> всех этих эффектов."
    : ""}
<br>
<br>
${PlayerProgress.realityUnlocked()
    ? `Эффекты Глифов имеют 2 других способа состыковки; их внутренний способ состыковки и способ состыковки со
      всеми остальными эффектами игры. Они могут не быть одинаковыми, например, эффект "Степень Измерений Антиматерии"
      будет состыкован <i>аддитивно сам с собой</i>, но когда общее количество эффекта состыкуется, оно будет сложено с
      базовым значением в ${formatInt(1)} и затем применено в качестве <i>эффекта степени</i> к Измерениям Антиматерии.`
    : ""}
`,
      isUnlocked: () => true,
      tags: ["effect", "stack", "combine", "add", "reduce", "multiply", "divide", "power", "dilation", "glyph"],
      tab: "options/gameplay"
    }, {
      name: "Common Abbreviations",
      textName: "Принятые Сокращения",
      info: () => `
Множество ресурсов внутри игры будет указываться в сокращенном формате для экономии места. Этот раздел
будет обновляться по мере открытия новых ресурсов.
<br>
- <b>АМ</b>: Антиматерия<br>
- <b>ИА</b>: Измерения Антиматерии<br>
- <b>ГА</b>: Галактика Антиматерии<br>
${PlayerProgress.infinityUnlocked() ? "- <b>ОБ</b>: Очко Бесконечности<br>" : ""}
${PlayerProgress.infinityUnlocked() ? "- <b>НЧ</b>: Нормальный Челлендж<br>" : ""}
${PlayerProgress.infinityUnlocked() ? "- <b>БЧ</b>: Бесконечный Челлендж<br>" : ""}
${InfinityDimension(1).isUnlocked || PlayerProgress.eternityUnlocked()
    ? "- <b>ИБ</b>: Измерение Бесконечности<br>"
    : ""}
${PlayerProgress.replicantiUnlocked() ? "- <b>ГР</b>: Галактика Репликантов<br>" : ""}
${PlayerProgress.eternityUnlocked() ? "- <b>ОВ</b>: Очко Вечности<br>" : ""}
${PlayerProgress.eternityUnlocked() ? "- <b>ВТ</b>: Временна́я Теорема<br>" : ""}
${PlayerProgress.eternityUnlocked() ? "- <b>ИВ</b>: Измерение Времени<br>" : ""}
${PlayerProgress.eternityUnlocked() ? "- <b>ВЧ</b>: Вечный Челлендж<br>" : ""}
${PlayerProgress.dilationUnlocked() ? "- <b>ТЧ</b>: Тахион Частица<br>" : ""}
${PlayerProgress.dilationUnlocked() ? "- <b>РВ</b>: Растянутое Время<br>" : ""}
${PlayerProgress.dilationUnlocked() ? "- <b>ГТ</b>: Галактика Тахионов<br>" : ""}
${PlayerProgress.realityUnlocked() ? "- <b>МР</b>: Машина Реальности<br>" : ""}
${PlayerProgress.realityUnlocked() ? "- <b>ОА</b>: Очко Автоматизатора<br>" : ""}
${PlayerProgress.realityUnlocked() ? "- <b>ЧД</b>: Чёрная Дыра<br>" : ""}
${MachineHandler.isIMUnlocked ? "- <b>ММ</b>: Мнимая Машина<br>" : ""}
${Laitela.isUnlocked ? "- <b>ТМ</b>: Тёмная Материя<br>" : ""}
${Laitela.isUnlocked ? "- <b>ТЭ</b>: Тёмная Энергия<br>" : ""}
`,
      isUnlocked: () => true,
      tags: ["abbreviation", "shorten", "am", "ad", "ag", "ip", "nc", "ic", "id", "rg", "ep", "tt", "td", "ec", "tp",
        "dt", "tg", "rm", "ap", "bh", "im", "dm", "de"],
      tab: ""
    }, {
      name: "Antimatter Dimensions",
      textName: "Измерения Антиматерии",
      info: () => `
Антиматерия - ресурс, используемый на протяжении всей игры для покупки различных вещей по мере прогрессии.
<br>
<br>
Измерения Антиматерии - средства производства. 1ое Измерение Антиматерии производит антиматерию,
каждое следующее Измерение Антиматерии производит предыдущее, обеспечивая устойчивый рост.
Всего существует восемь Измерений Антиматерии.
<br>
<br>
<b>Множитель Измерений:</b> У каждого Измерения есть свой множитель (например: 1ое Измерение ${formatX(1, 1, 1)}).
Базовое производство данного Измерения умножается на это число.
Этот множитель увеличивается в ${formatX(2)} раза за каждые ${formatInt(10)} покупок данного Измерения.
Каждый раз, когда это происходит, цена измерения увеличивается.
<br>
<br>
<b>Накопленное Количество Измерений:</b> Слелующий столбец - текущее количество данного измерения, которое вы имеете.
Это - комбинация того, что вы купили за антиматерию, и того, что было произведено более высокими Измерениями.
<br>
<br>
<b>Процент Роста Измерений:</b> Это число показывает рост этого Измерения каждую секунду. ${formatPercents(1)} означает,
что эти измерения удваиваются каждую секунду.
<br>
<br>
<b>Макс Всё:</b> Макс Всё позволяет купить максимальное количество Измерений и Улучшений Скорости Тика.
Сначала покупаются все 1ые Измерения, затем все 2ые, затем 3ьи и так до 8ого. Потом покупаются Улучшения Скорости Тика.
<br>
<br>
<b>Базовые Цены Измерений Антиматерии:</b> ${Array.range(1, 8)
    .map(tier => format(AntimatterDimension(tier)._baseCost, 2, 2))
    .join(", ")}
<br>
<b>Базово за ${formatInt(10)} купленных измерений цена увеличивается:</b> ${Array.range(1, 8)
  .map(tier => format(AntimatterDimension(tier)._baseCostMultiplier, 2, 2))
  .join(", ")}
<br>
<br>
<b>Горячие клавиши: 1, 2, 3, 4, 5, 6, 7, 8</b> для покупки до ${formatInt(10)} энного Измерения
(вы также можете зажиать Shift при покупке Измерений, что купит только
${formatInt(1)} вместо ${formatInt(10)}), <b>M/Ь</b> для Макс Всё
`,
      isUnlocked: () => true,
      tags: ["dims", "normal", "antimatter", "ad"],
      tab: "dimensions/antimatter"
    }, {
      name: "Tickspeed",
      textName: "Скорость Тика",
      info: () => `
Производство в игре происходит каждый "тик", который изначально случается раз в секунду. За покупку Улучшений Скорости
Тика вы можете заставить ваши Измерения Антиматерии производить быстрее, как если бы несколько тиков случались за одну
секунду.
<br>
<br>
<b>Скорость Тика:</b> Демонстрирует как много игровых тиков происходит каждую секунду. Дробные тики учитываются и
дают бонус к производству. Заметьте, что игра всегда производит вычисления с той скоростью, которая указана во вкладке
Настроек.
<br>
<br>
<b>Цена:</b> Цена в антиматерии за умножение тиков/сек на показанное значение.
(без Галактик это ${formatX(1.1245, 0, 3)} за покупку)
<br>
<br>
<b>Купить Макс:</b> Купит максимальное количество доступных Улучшений Скорости Тика.
<br>
<br>
<b>Горячие Клавиши: T/Е</b> купить как можно больше Улучшений Скорости Тика, или <b>Shift+T/Shift+Ь</b> чтобы купить
одно улучшение. <b>M/Ь</b> для Макс Всё.
`,
      isUnlocked: () => Tickspeed.isUnlocked,
      tags: ["dimension", "earlygame", "time"],
      tab: "dimensions/antimatter"
    }, {
      name: "Dimension Boosts",
      textName: "Бусты Измерений",
      info: () => `
<b>Буст Измерения:</b> Сбрасывает вашу антиматерию и все Измерения Антиматерии, но открывает новое Измерение
Антиматерии и даёт бонусы к множителям Измерений.
1ый Буст Измерения Требует ${formatInt(20)} 4ых Измерений, 2ой требует ${formatInt(20)} 5ых Измерений и т.д.
После разблокировки всех ${formatInt(8)} Измерений,
каждый дополнительный буст будет стоить на ${formatInt(15)} 8ых Измерений больше предыдущего Буста и не будет
разблокировать новое Измерение, но продолжит увеличивать множители Измерений Антиматерии.
<br>
<br>
Первый Буст Измерений даёт ${formatX(2)} множитель к 1ым Измерениям. Второй умножает 1ые и 2ые Измерения, третий
1ые, 2ые и 3ьи, и так далее. Восьмой Буст и следующие увеличивают множитель всех Измерений Антиматреии. 
Например, с ${formatInt(3)} Бустами, 1ое Измерение получит множитель ${formatX(8)}, 2ое ${formatX(4)},
3ье ${formatX(2)}, а все остальные будут без множителя.
<br>
<br>
<b>Горячая Клавиша: D/В</b> попытается купить Буст Измерений.
`,
      isUnlocked: () => true,
      tags: ["dimboost", "reset", "earlygame"],
      tab: "dimensions/antimatter"
    }, {
      name: "Antimatter Galaxies",
      textName: "Галактики Антиматерии",
      info: () => `
Покупка Галактики Антиматерии сбросит игру до момента, когда доступно только ${formatInt(4)} Измерения, но
увеличит эффект от Улучшений Скорости Тика на +${format(0.02, 0, 2)} за первые две Галактики.
<br>
<br>
Несмотря на то, что это будет иметь очень маленькое воздействие на первые несколько улучшений,
увеличение мультипликативно и вскоре будет заметно.
<br>
<br>
Ваша первая Галактика Антиматерии стоит ${formatInt(80)} Восьмых Измерений, каждая следующая будет стоить на
${formatInt(60)} Измерений больше.
<br>
<b>Масштабирование Далёких Галактик:</b> После ${formatInt(100)} Галактик Антиматерии, увеличение цены между Галактиками
будет повышаться на ${formatInt(2)} за Галактику, делая цену следующей Галактики на ${formatInt(62)} больше, затем
на ${formatInt(64)} и так далее.
<br>
<b>Отдалённое Масштабирование Галактик:</b> После ${formatInt(Galaxy.remoteStart)} Галактик Антиматерии, <i>общая</i>
цена увеличивается ещё на ${formatPercents(0.002, 1)} за Галактику, плюсом к Далёкому масштабированию.
<br>
<br>
<b>Горячая Клавиша: G/П</b> попытается купить Галактику Антиматерии.
`,
      isUnlocked: () => true,
      tags: ["8th", "reset", "galaxy", "earlygame"],
      tab: "dimensions/antimatter"
    }, {
      name: "Dimensional Sacrifice",
      textName: "Жертвование Измерений",
      info: () => `
<b>Вы разблокируете Жертвование Измерений после пятого Буста Измерений.</b>
<br>
<br>
Жертвование мгновенно сбросит имеющееся количество всех не Восьмых Измерений до нуля, без уменьшения множителя
или текущей цены. Взамен, оно умножит Множитель Восьмого Измерения на указанное значение.
Восстановление производства займёт некоторое время, но в итоге вы получите большую мощность производства.
<br>
<br>
Множитель Жертвования Измерений масштабируется с количеством 1ых Измерений, которые были у вас на момент жертвования,
а масштабирование может быть увеличено за счёт прохождения определённых челленджей и Достижений. Множитель сохраняется
между жертвованиями. Это означает, что жертвование один раз при ${formatX(10)} и затем при ${formatX(4)} - то же самое,
что жертвование при ${formatX(8)} и затем ${formatX(5)}; в обоих случаях общий множитель жертвования будет равен
${formatX(40)}.
<br>
<br>
<b>Горячая Клавиша: S/Ы</b> попытается сделать Жертвование.
`,
      isUnlocked: () => Sacrifice.isVisible,
      tags: ["8th", "reset", "earlygame", "gods", "earlygame"],
      tab: "dimensions/antimatter"
    }, {
      name: "Achievements",
      textName: "Достижения",
      info: () => `
Каждое Достижение имеет требование для разблокировки. После разблокировки некоторые Достижения дают награду.
Требования и награды значительно различаются по сложности и выгоде.
<br>
<br>
В дополнение к определённым наградам, за каждое Достижение вы получаете ${formatX(1.03, 2, 2)} множитель
ко всем Измерениям Антиматерии. Каждый полностью разблокированный ряд Достижений даёт дополнительный множитель в ${
  formatX(1.25, 2, 2)}. Общий эффект множителя всех Достижений показан над всеми картинками Достижений.
<br>
<br>
Секретные Достижения не дают никаких преимуществ или усилений и существуют ради забавы. Наведите мышкой на Секретное
Достижение, чтобы получить подсказку о его разблокировке.
`,
      isUnlocked: () => true,
      tags: ["earlygame", "awards", "earlygame"],
      tab: "achievements"
    }, {
      name: "Infinity",
      textName: "Бесконечность",
      info: () => `
Как только количество имеющейся у вас антиматерии превышает возможности мира, (${
  formatInt(2)}<sup>${formatInt(1024)}</sup> или примерно ${formatPostBreak(Number.MAX_VALUE, 6)},
иногда называемого "Бесконечность"), вы будете обязаны сделать “Большой Хруст”. Это сбросит вашу антиматерию,
Измерения Антиматерии, Бусты Измерений и Галактики Антиматерии.
<br>
<br>
В скором времени вы сможете преодолеть значение в ${formatPostBreak(Number.MAX_VALUE, 6)}, но до тех пор любые числа,
большие чем это, будут обозначаться как ${format(Infinity)}.
<br>
<br>
Каждая пройденная Бесконечность даст вам Очко Бесконечноссти, которое может быть потрачено на улучшения во вкладке
Бесконечности. Эти улучшения покупаются сверху вниз. Вы также получите одну "Бесконечность", что является количеством
Больших Хрустов, которые вы совершили.
<br>
<br>
Улучшение "Умножить Очки Бесконечности со всех источников на ${formatInt(2)}" может быть куплено множество раз,
но каждая покупка требует в ${formatInt(10)} раз больше ОБ.
Вы должны получить Достижение "ДЛЦ не требуется" для покупки этого улучшения.
<br>
<br>
<b>Горячая Клавиша: C/С</b> попытается сделать Большой Хруст.
`,
      isUnlocked: () => PlayerProgress.infinityUnlocked(),
      tags: ["crunch", "big", "upgrades", "ip", "reset", "prestige", "earlygame"],
      tab: "infinity/upgrades"
    }, {
      name: "Normal Challenges",
      textName: "Нормальные Челленджи",
      info: () => `
Нормальные Челленджи разблокируются поле вашей первой Бесконечности; они меняют внутриигровые механики разными
способами чтобы создать более сложные обстоятельства для Бесконечности. Чтобы пройти челлендж, вы вновь должны
достигнуть ${formatPostBreak(Number.MAX_VALUE, 2)} антиматерии.
<br>
<br>
Каждый пройденный Нормальный Челлендж даст АвтоПокупщик или возможность улучшить существующий АвтоПокупщик.
Вы можете запускать Челленджи множество раз (награда даётся только один), и из них можно выйти в любой момент,
нажав на кнопку "Покинуть Челлендж".
<br>
<br>
Ваша первая Бесконечность считается первым Нормальным Челленджем, и поэтому он уже пройден к моменту,
когда вы разблокируете челленджи.
<br>
<br>
Самая правая колонка Улучшений Бесконечности не работает внутри челленджей.
`,
      isUnlocked: () => PlayerProgress.infinityUnlocked(),
      tags: ["infinity", "autobuyer", "earlygame"],
      tab: "challenges/normal"
    }, {
      name: "Autobuyers",
      textName: "АвтоПокупщики",
      info: () => `
АвтоПокупщики позволяют вам автоматически покупать измерения, улучшения или престижи. Всё управление АвтоПокупщиками
осуществляется через подвкладку "АвтоПокупщики" во вкладке "Автоматизация", включая все дополнительные АвтоПокупщики,
которые откроются позднее, по мере развития в игре.
<br>
<br>
АвтоПокупщики Измерений Антиматерии и АвтоПокупщик Улучшений Скорости Тика могут разблокируются на основе общего
количества антиматерии, но большинство других АвтоПокупщиков будут требовать покупку улучшений или прохождение
челленджей для разблокировки.
<br>
<br>
У большинства АвтоПокупщиков есть следующие параметры:
<br>
<br>
<b>Интервал АвтоПокупщика:</b> Время перезарядки, перед тем как АвтоПокупщик попытается сделать ещё одну покупку.
АвтоПокупщики Измерений Антиматерии и АвтоПокупщик Улучшения Скорости Тика требуют прохождение соответствующих
челленджей для разблокировки возможности улучшения интервала.
<br>
<br>
<b>Объёмная Покупка АвтоПокупщиков Измерений Антиматерии:</b> Как только интервал АвтоПокупщика достигает максимума
(${formatInt(100)} мс), все будущие улучшения удвоят количество, которое этот АвтоПокупщик покупает за тик.
Это может быть отключено.
<br>
<br>
<b>Количество Покупок АвтоПокупщика Измерений:</b> АвтоПокупщики Измерений могут быть установлены на покупку одиночных
Измерений, или до ${formatInt(10)}. Объёмная покупка отключена, когда АвтоПокупщик устанвлен на одиночный режим.
<br>
<br>
<b>Количество Покупок АвтоПокупщика Скорости Тика :</b> АвтоПокупщик Скорости Тика может быть настроен на покупку
одного улучшения за активацию или на покупку максимума возможных улучшений после прохождения Челленджа 9.
<br>
<br>
<b>Кастомизация Автоматических Бустов Измерений:</b> С АвтоПокупщиком Бустов Измерений вы можете установить количество
Бустов, которое он попытается купить, минимальное количество Галактик Антиматерии до того как Бусты будут автоматически
покупаться, и (разблокируется позже) возможность покупать определённое количество Бустов Измерений за раз. Если вы
достигли определённого количества Галактик, АвтоПокупщик будет игнорировать лимит Бустов.
<br>
<br>
<b>Макс Галактики:</b> Наибольшее количество Галактик, которое АвтоПокупщик Галактик будет покупать.
<br>
<br>
<b>ОБ за Хруст:</b> После Преодоления Бесконечности, вы можете установить как много ОБ вы хотите получить, перед тем
как сделать Хруст.
<br>
<br>
<b>АвтоПокупщик Жертвования:</b> Этот АвтоПокупщик начинается с максимизированным интервалом, потенциально срабатывая
каждый тик.
<br>
<br>
<b>Динамическое Значение:</b> Улучшенные АвтоПокупщики престижа имеют режим, который делает престиж, когда определённый
порог пройден. Включение "Динамического Значения" позволит значению этой задержки быть автомитечески увеличенной после
открытия определённых улучшений или достижений, которые применяют множитель к этому значению.
<br>
<br>
<b>Пауза/Продолжение АвтоПокупщиков:</b> Эта кнопка поставит на паузу или продолжит АвтоПокупщики, которые включены.
Она не меняет индивидуальные натройки АвтоПокупщиков. Думайте о ней как о главном переключателе.
<br>
<br>
<b>Включение/Выключение Всех АвтоПокупщиков:</b> Эта кнопка включит или выключит все ваши АвтоПокупщики индивидуально.
<br>
<br>
<b>Горячая клавиша: A/Ф</b> (для паузы/снятия с паузы).
Дополнительно, задерживание <b>Alt</b> при нажатии горячей клавиши, ассоциируемой с улучшением, измерением или престижем
переключит соответствующий АвтоПокупщик.
`,
      isUnlocked: () => player.records.totalAntimatter.gte(1e40),
      tags: ["infinity", "automation", "challenges", "rewards", "interval", "earlygame"],
      tab: "automation/autobuyers"
    }, {
      name: "Break Infinity",
      textName: "Преодолеть Бесконечность",
      info: () => `
Как только вы Преодолеваете Бесконечность, вы больше не ограничены ${formatPostBreak(Number.MAX_VALUE, 2)} антиматерии
и можете зарабатывать более чем ${formatInt(1)} ОБ за Хруст, основываясь на том, насколько больше антиматерии у вас было
на момент Хруста.
<br>
<br>
Теперь вы получаете ~${format(1.78, 2, 2)} ОБ за Хруст при ${formatPostBreak(Number.MAX_VALUE, 2)} антиматерии. ОБ,
которые вы зарабатываете за Хруст умножаются на ${formatInt(10)} за каждый дополнительный коэффициент
${formatPostBreak(Number.MAX_VALUE, 2)} антиматерии, которую вы зарабатываете (в продолжительной манере). Это
округляется до ближайшего целого числа <i>после</i> применения всех множителей.
<br>
<br>
Цена антиматерии всех Измерений начинает увеличиваться быстрее после того, как она преодолевает
${formatPostBreak(Number.MAX_VALUE, 2)}. Цена <i>между</i> улучшениями будет увеличиваться на ${formatX(10)}
<i>за улучшение</i> после ${formatPostBreak(Number.MAX_VALUE, 2)}, и похожее масштабирование происходит с
Улучшениями Скорости Тика.
`,
      isUnlocked: () => Autobuyer.bigCrunch.hasMaxedInterval || PlayerProgress.eternityUnlocked(),
      tags: ["limit", "crunch", "upgrades", "midgame"],
      tab: "infinity/break"
    }, {
      name: "Infinity Dimensions",
      textName: "Измерения Бесконечности",
      info: () => `
<b>Разблокировка Измерений Бесконечности:</b> Измерения Бесконечности разблокируются путём достижения определённого
количества Антиматерии.
<br>
<br>
<b>Покупка Измерений Бесконечности:</b> Измерения Бесконечности можно купить только по ${formatInt(10)} штук, за
Очки Бесконечности. Они дают постоянный множитель за покупку, так же как другие Измерения. Настоящий применённый
множитель зависит от того, какие Измерения Бесконечности вы покупаете. <!-- Извини, Garnet :/ -->
<br>
<br>
<b>Производство Измерений Бесконечности:</b> Так же как Измерения Антиматерии, каждое Измерение Бесконечности производит
следующее Измерение Бесконечности.
<br>
<br>
Каждый Хруст, произведённые Измерения Бесконечности сбрасывают до количеств, которое вы купили. Хотя производство
Измерений Бесконечности не перемещается между Хрустами, но все множители, которые вы получили за покупку, перемещаются.
<br>
<br>
<b>Пороги разблокировки Измерений Бесконечности (антиматерии):</b> ${Array.range(1, 8)
    .map(tier => formatPostBreak(InfinityDimension(tier)._unlockRequirement))
    .join(", ")}
<br>
<b>Множители за покупку Измерений Бесконечности:</b> ${Array.range(1, 8)
    .map(tier => format(InfinityDimension(tier)._powerMultiplier))
    .join(", ")}
<br>
<b>Базовые цены Измерений Бесконечности (ОБ):</b> ${Array.range(1, 8)
    .map(tier => format(InfinityDimension(tier)._baseCost))
    .join(", ")}
<br>
<b>Увеличение цен Измерений Бесконечности:</b> ${Array.range(1, 8)
    .map(tier => format(InfinityDimension(tier)._costMultiplier))
    .join(", ")}
<br>
<br>
Вместо антиматерии, 1ое Измерение Бесконечности производит Мощность Бесконечности, которая даёт множитель ко всем
Измерениям Антиматерии, равный (степень<sup>${formatInt(7)}</sup>). Улучшения Скорости Тика не влияют на
Измерения Бесконечности.
`,
      isUnlocked: () => Autobuyer.bigCrunch.hasMaxedInterval || PlayerProgress.eternityUnlocked(),
      tags: ["id", "power", "new", "dims", "unlock", "break", "midgame"],
      tab: "dimensions/infinity"
    }, {
      name: "Infinity Challenges",
      textName: "Бесконечные Челленджи",
      // This one could use some work!
      info: () => `
Бесконечные Челленджи почти такие же, как Нормальные Челленджи, но они имеют более высокую цель. Вместо разблокировки
АвтоПокупщиков, они дают бонусы к различным формам производство. Так же, как в Нормальных Челленджах, самая правая
колонка Улучшений Бесконечности отключена внутри Бесконечных Челленджей.
<br>
<br>
Бесконечные Челленджи требуют достижения определённого количества антиматерии, прежде чем вы сможете запустить их.
<br>
<br>
<b>Пороги разблокировки Бесконечных Челленджей:</b> ${GameDatabase.challenges.infinity
    .map(ic => formatPostBreak(ic.unlockAM)).join(", ")}
`,
      isUnlocked: () => Autobuyer.bigCrunch.hasMaxedInterval || PlayerProgress.eternityUnlocked(),
      tags: ["rewards", "break", "ic", "midgame"],
      tab: "challenges/infinity"
    }, {
      name: "Replicanti",
      textName: "Репликанты",
      info: () => `
Репликанты - ещё один ресурс, разблокирующийся на ${format(DC.E140)} ОБ. Вместо производства чего-то ещё, Репликанты
производят <i>сами себя</i>, до максимума в ${formatPostBreak(Number.MAX_VALUE, 2)}. Улучшения Скорости Тика не влияют
на скорость производства Репликантов. Каждый отдельный Репликант имеет определённый шанс
(изначально ${formatPercents(0.01)}) на производство другого Репликанта каждый тик Репликантов (изначально раз в 
секунду). Оба этих значения могут быть улучшены за ОБ.
<br>
<br>
Если вы купили улучшение Галактики Репликантов, то вы можете получить "бесплатную" Галактику Репликантов в обмен на
сброс количества ваших Репликантов до ${formatInt(1)}. Эта Галактика бесплатна в том, что она действует как Галактика
Антиматерии, но при этом не увеличивает цену покупки настоящих Галактик Антиматерии. Не смотря на это, она сбросит те
же самые вещи, что и Галактика Антиматерии.
<br>
<br>
<b>Горячая Клавиша: R/К</b> попытается купить Галактику Репликантов.
<br>
Репликанты дают множитель ко всем Измерениям Бесконечности, который достигнет максимума в
${formatX(Math.pow(2, 20), 2, 2)} при ${formatPostBreak(Number.MAX_VALUE, 2)} Репликантов.
<br>
<br>
<b>Цена улучшения Шанса:</b> Базово ${format(DC.E150)} ОБ, увеличение ${formatX(DC.E15)} ОБ
<br>
<b>Цена улучшения Интервала:</b> Базово ${format(DC.E140)} ОБ, увеличение ${formatX(DC.E10)} ОБ
<br>
<b>Цена улучшения Галактики:</b> Базово ${format(DC.E170)} ОБ, увеличение ${formatX(DC.E25)} ОБ и дополнительно
${formatX(1e5)} ОБ за улучшение, масштабирование похоже на далёкие Галактики Антиматерии. После ${formatInt(100)}
Галактик Репликантов, это ${formatX(1e5)} за улучшение меняется на ${formatX(DC.E55)}. После ${formatInt(1000)},
масштабирование переключается с квадратного на кубическое, с множителем ${formatX(DC.E55)}, увеличивающемся
на ${formatX(DC.E5)} за улучшение.
`,
      isUnlocked: () => Replicanti.areUnlocked || PlayerProgress.eternityUnlocked(),
      tags: ["interval", "chance", "infinity", "galaxy", "galaxies", "midgame"],
      tab: "infinity/replicanti"
    }, {
      name: "Eternity",
      textName: "Вечность",
      info: () => `
После достижения ${formatPostBreak(Number.MAX_VALUE, 2)} ОБ, вы можете перейти в новую Вечность. Вечности сбрасывают
всё, до текущего момента, кроме рекордов челленджей, Достижений и всего в разделе Основное во вкладке Ститистики. Вы
сможете доступ к большему количеству контента после вашей первой Вечности.
<br>
<br>
Вы сможете преодолеть ${formatPostBreak(Number.MAX_VALUE, 2)} ОБ. Чем больше Очков Бесконечности вы наберёте перед тем,
как перейти в новую Вечность, тем больше Очков Вечности получите. Вы также получаете "Вечность" за прохождение Вечности.
<br>
<br>
Заработок Очков Вечности масштабируется похожим с масштабированием Очков Бесконечности образом.
Базовое количество заработанных ОВ на ${formatPostBreak(Number.MAX_VALUE, 2)} ОБ равно ~${format(1.62, 2, 2)} ОВ,
умноженное на
${formatInt(5)} за каждый коэффициент ${formatPostBreak(Number.MAX_VALUE, 2)} ОБ, который у вас есть. Это всегда
округляется вниз, что означает, что вы поучите ${formatInt(1)} ОВ при ${formatPostBreak(Number.MAX_VALUE, 2)} ОБ, но
не достигните ${formatInt(2)} ОВ до ${formatPostBreak(DC.E349)}.
<br>
<br>
<b>Hotkey: E/У</b> попытается сделать сброс Вечности.
`,
      isUnlocked: () => PlayerProgress.eternityUnlocked(),
      tags: ["eternal", "ep", "reset", "prestige", "midgame"],
      tab: "eternity/upgrades"
    }, {
      name: "Eternity Milestones",
      textName: "Рубежи Вечности",
      info: () => `
Чтобы ускорить Вечности и сделать их более удобными, вы, с постепенным набором "Вечностей", будете открывать различные
улучшения. Обычно они позволят вам начать с определёнными улучшениями, которые вы в обратном случае потеряли бы
после Вечности, дадут вам АвтоПокупщики или способ пассивно зарабатывать ресурсы офлайн.
<br>
<br>
Все новые АвтоПокупщики будут иметь переключатели рядом с их ручными кнопками (например, АвтоПокупщики Измерений
Бесконечности могут быть найдены во вкладке Измерений Бесконечности), в дополнение к их разделам во вкладке 
Автоматизации.
Улучшения к АвтоПокупщикам Бустов Измерений, Галактик Антиматерии и Большого Хруста обновляют уже существующие
разделы во вкладке Автоматизации.
<br>
<br>
Рубежи пассивной генерации работают только офайн, и имеют определённые условия в настройке АвтоПокупщиков для
корректной работы, что указано на вкладке рубежей.
`,
      isUnlocked: () => PlayerProgress.eternityUnlocked(),
      tags: ["eternities", "rewards", "automation", "midgame"],
      tab: "eternity/milestones"
    }, {
      name: "Time Dimensions",
      textName: "Измерения Времени",
      info: () => `
После первой Вечности, вы открываете Измерения Времени. Вы покупаете их за Очки Вечности, и они производят Осколки
Времени, дающие Улучшения Скорости Тика. Эти Улучшения Скорости Тика работают как обычные Улучшения, но не увеличивают
цену их покупки. Измерения Времени, Осколки Времени и Улучшения Скорости Тика, которые они дают, сохраняются после
Бесконечности, но сбрасываются после Вечности.
<br>
<br>
Измерения Времени работают так же, как и другие Измерения. Схожим с Измерениями Бесконечности образом, ваше производство
будет сброшено до количества, которое вы купили после каждой Вечности, но вы сохраните все улучшения множителей, которые
вы купили.
<br>
<br>
Каждая покупка увеличивает множитель этого конкретного Измерения Времени на ${formatX(4)}. Множитель цены между
улучшениями имеет базовое значение, но оно увеличивается на ${formatX(1.5, 1, 1)} при
${format(TimeDimension(1)._costIncreaseThresholds[0], 2)} ОВ, и на ${formatX(2.2, 1, 1)} (от базового значения) при
${format(TimeDimension(1)._costIncreaseThresholds[1])} ОВ. Эти увеличения применяются ретроактивно, заставляя цену
скакать, когда они достигли соответствующих порогов, и применяются только к первым четырём измерениям. После
${format(TimeDimension(1)._costIncreaseThresholds[2])} ОВ каждая покупка считается за четыре для увеличения цены,
приводя к тому, что увеличение цены происходит значительно быстрее.
<br>
<b>Базовая цена Измерений Времени (ОВ):</b> ${Array.range(1, 8)
    .map(tier => format(TimeDimension(tier)._baseCost))
    .join(", ")}
<br>
<b>Базовое увеличение цены Измерений Времени:</b> ${Array.range(1, 8)
    .map(tier => format(TimeDimension(tier)._costMultiplier))
    .join(", ")}
<br>
<br>
Каждый порог для заработка Улучшения Скорости Тика требует на ${formatPercents(0.33)} больше Осколков Времени, чем
предыдущий, или ${formatPercents(0.25)} с соотсутвтвующим Временны́м Исследованием. После
${formatInt(FreeTickspeed.softcap)} улучшений, множитель между каждым бесплатным Улучшением Скорости Тика будет
постепенно повышен со скоростью в ~${formatX(1.35, 0, 2)}
за ${formatInt(50000)} улучшений (${formatX(1.000006, 0, 6)} за улучшение).
`,
      isUnlocked: () => PlayerProgress.eternityUnlocked(),
      tags: ["dims", "td", "shards", "eternity", "midgame"],
      tab: "dimensions/time"
    }, {
      name: "Time Studies",
      textName: "Временны́е Исследования",
      info: () => `
Временно́е Исследование - мощное улучшение, которое покупается за Временны́е Теоремы.
<br>
<br>
Временны́е Теоремы - лимитированный ресурс, который с каждым разом стоит всё больше. Они могут быть куплены за
антиматерию, Очки Бесконечности или Очки Вечности. Их цена увеличивается на определённый коэффициент за каждую покупку.
Временны́е Теоремы не сбрасываются после Вечности.
<br>
<br>
Исследования расположены в виде древа, где вы обязаны купить предыдущие Исследования, для того чтобы купить следующее.
Однако, для этого правила существует три исключения:
<br>
Там, где линия между исследованиями имеет цвет, вы можете выбрать только один из трёх путей.
<br>
Когда на пути расположено Исследование для Вечного Челленджа, вам необходимо пройти все Челленджи, соединённые с ним,
хотя бы единожды, для того чтобы разблокировать исследование. Вам не нужно покупать исследование челленджа, для того
чтобы разблокировать необходимое исследование.
<br>
Ближе к концу, где все края соединяются вновь, вы можете выбрать только одно исследование из двух.
<br>
<br>
Вы можете зажать shift и затем кликнуть на Временно́е Исследование, чтобы купить все Исследования до текущего.
Это может купить не те исследования, которые вы хотели, если вы кликните с шифтом на исследование, чтобы получить доступ
к которому, необходимо выбрать между двумя или более различными опциями, которые вы не можете получить одновременно
(смотри выше). Shift+клик покупает все исследования на пути, приобретая как можно больше на каждом ряду, прежде чем
двигаться ниже по древу.
<br>
<br>
<b>Пресеты:</b> Кнопки, изначально названные от 1 до 6, позволяющие вам сохранить текущий набор исследований в слот,
позволяя вам быстро купить этот набор исследований вновь одним нажатием мыши. Пресеты могут быть переназваны, но вы
не можете называть разные пресеты одним именем.
<br>
<br>
<b>Импорт Древа/Изменить Пресет:</b> При изменении пресета или импорте Древа Исследований, появится меню,
демонстрирующее Временны́е Исследования, которые будут куплены. Для разделённых путей, вы можете использовать
сокращения, вместо наборов исследований. Дополнительно, если строка Временны́х Исследований содержит
допустимый Вечный Челлендж, добавление "!" в конец строки заставит игру попытаться моментально разблокировать и
войти в указанный Вечный Челлендж при использовании.
<br>
<br>
<b>Предпочтения:</b> Клик по иконке шестерёнки откроет окно, которое позволит вам выбрать "стандартные" пути на
развилках. Выбор стандартного пути изменит поведение клика с зажатой клавишей Shift. Вы можете выбрать два  пути на
развилке Измерений, если вы имеете соответствующее Временно́е Исследование.
<br>
<br>
<b>Сброс:</b> Сброс позволяет вам сбросить исследования, которые вы купили, и вернёт вам все Временны́е Теоремы, которые
вы на них потратили. Это делается бесплатно, но срабатывает только после завершения Вечности.
<br>
<br>
<b>Цены для Временны́х Теорем:</b>
<br>
<b>Антиматерия:</b> Изначально ${format(DC.E20000)}, ${formatX(DC.E20000)} за Теорему
<br>
<b>Очки Бесконечности:</b> Изначально ${formatInt(1)}, ${formatX(DC.E100)} за Теорему
<br>
<b>Очки Вечности:</b> Изначально ${formatInt(1)}, ${formatX(2)} за Теорему
`,
      isUnlocked: () => PlayerProgress.eternityUnlocked(),
      tags: ["eternity", "ts", "theorems", "tree", "study", "midgame"],
      tab: "eternity/studies"
    }, {
      name: "Eternity Challenges",
      textName: "Вечные Челленджи",
      info: () => `
Вечные Челленджи - другой набор челленджей, разблокируемых внутри Древа Исследований. Они требуют определённого
количества Временны́х Теорем, а также дополнительного условия, которое вы должны удовлетворить для разблокировки
Челленджа.
<br>
<br>
Когда вы входите в Вечный Челлендж, вашей целью становится достежение определённого количества ОБ. После прохождения
Челленджа, вам не нужно иметь исследование данного челленджа для того, чтобы использовать его награду.
<br>
<br>
Только один Вечный Челлендж может быть разблокирован за раз.
<br>
<br>
Вы можете проходить один Вечный Челлендж 5 раз. После каждого прохождения увеличивается награда и сложность
следующего прохождения. Дополнительно, Вторичное требование разблокировки также увеличится. Цена Временны́х Теорем
остаётся той же.
<br>
<br>
Удовлетворение вторичного требования Вечного Челленджа разблокирует покупку данного челленджа до тех пор, пока вы его
не пройдёте. Это означает, что вам необходимо удовлетворить вторичное требование лишь <i>однажды</i>.
Как результат, вы можете разблокировать Вечный Челлендж с одним набором исследований, затем сброситься в другой набор
для прохождения данного Челленджа. ВЧ11 и ВЧ12 - исключения, требование развилки остаётся, даже если вы сбросили
свои исследования.
`,
      isUnlocked: () => PlayerProgress.eternityUnlocked(),
      tags: ["ec", "study", "time", "rewards", "completions", "midgame"],
      tab: "challenges/eternity"
    }, {
      name: "Time Dilation",
      textName: "Растяжение Времени",
      info: () => `
Растяжение Времени разблокируется когда вы покупаете соответствующее Временное Исследование.
Для того, чтобы купить это Исследование, вам потребуется ${formatInt(5000)} неиспользованных ВТ с
<i>общим</i> количеством ВТ в ${formatInt(TimeStudy.dilation.totalTimeTheoremRequirement)}, и 5 раз пройденными
ВЧ11 и ВЧ12 каждый.
<br>
<br>
Растяжение времени начнёт измененную Вечность, называемую Растяжение Времени. В ней <i>экспоненты</i> всех множителей
Измерений Антиматерии/Бесконечности/Времени и скорости тика будут возведены в степень
${formatPow(0.75, 2, 2)}, что значительно их ослабит. Если вы достигните ${formatPostBreak(Number.MAX_VALUE, 2)} ОБ
для прохождения Растянутой Вечности, вы будете награждены ресурсом, называемым Тахион Частицы.
<br>
<br>
Вы можете Растягиваться столько раз, сколько захотите, но Тахион Частицы не могут быть "размножены" подобно другим
ресурсам. Тахион Частицы никогда не уменьшаются, только повышаются, так как они повышаются до ограничения, основанного
на ваших множителях ТЧ и антиматерии, заработанной внутри Растяжения. Как результат, вы обычно не можете увеличить ваши
ТЧ до тех пор, пока не получите множитель ТЧ или возможность значительно увеличить антиматерию внутри Растяжения.
<br>
<br>
Тахион Частицы генерируют ещё одну валюту, называемую Растянутое Время. Растянутое Время переводится в Галактики
Тахионов при достижении определённых порогов, схожим с Улучшениями Скорости Тика от Измерений Времени образом. Эти
Галактики Тахионов похожи на Галактики Репликантов в том, что они влияют на скорость тика так же, как Галактики
Антиматерии, но при этом не повышают цену Галактик Антиматерии.
<br>
<br>
Разблокировка Растяжения Времени также разблокирует улучшения, которые вы можете купить за Растянутое Время. Первое и
третье улучшение в первом ряду Улучшений Растяжения могут быть куплены много раз. Второе улучшение тоже может быть
куплено много раз, но в конечном итоге достигает лимита.
`,
      isUnlocked: () => DilationTimeStudyState.studies[1].isBought || PlayerProgress.realityUnlocked(),
      tags: ["dial", "dt", "dilated", "tachyon", "particle", "study", "free", "galaxy", "galaxies", "midgame"],
      tab: "eternity/dilation"
    }, {
      name: "Reality",
      textName: "Реальность",
      info: () => `
Когда вы достигаете ${formatPostBreak(DC.E4000)} ОВ и получили первые
${formatInt(13)} рядов Достижений, вы сможете купить Временны́е Исследования, разблокирующие Реальность.
Её разблокировка откроет новую вкладку, где вы сможете найти кнопку, создающую новую Реальность. Начало новой Реальности
почти полностью сбросит игру до текущего момента, но взамен даст новую валюту - Машины Реальности, а также
Глиф и Очко Перка.
<br>
<br>
Вы также потеряете первые ${formatInt(13)} рядов Достижений и все их награды. Однако вы сохраните все значения в
разделе Основное во вкладке Статистики и все ваши рекорды челленджей.
<br>
<br>
После прохождения первой Реальности, вкладка Глифов будет содержать кнопку, которая позволит вам перезапустить
Реальность без изменений к предстоящим Глифам. <b>Заметьте, что этот сброс не даст вам никаких наград, даже если вы
в ином случае получили бы их за завершение Реальности.</b>
<br>
<br>
Вам необходимо вновь выполнить требование каждого Достижения для того чтобы получить награду, но вы также пассивно
разблокируете следующее непройденное достижение каждые ${timeDisplayNoDecimals(30 * 60000)} без каких-либо действий,
даже если вы не удовлетворили требование данного Достижения. Это автоматическое прохождение может быть отключено,
в таком случае таймер досчитает до 0 и остановится, мгновенно выполняя следующее достижение после включения.
Таймер продолжает работать в офлайне.
<br>
<br>
Машины Реальности могут быть потрачены на различные улучшения во вкладке Реальности и являются главным ресурсом
после разблокировки Реальности. Глифы - это экипируемые объекты, которые необходимо экипировать для получения
их бонусов. Очки Перка - ещё одна валюта, которая может быть потрачена в подвкладке Перков. На неё можно купить
различные Перки.
<br>
<br>
Машины Реальности масштабируются исключительно с ОВ. Кнопка Реальности покажет вам как много ОВ требуется для
получения на 1 МР больше. Первые ${formatInt(10)} МР масштабируются линейно по экспоненте между
${formatPostBreak(DC.E4000)} ОВ и ${formatPostBreak(DC.C10P16000D3)} ОВ, а затем после
МР = ${formatInt(1000)}<sup>log<sub>${formatInt(10)}</sub>(ОВ)/${formatInt(4000)}-${formatInt(1)}</sup>. Эта формула
увеличивает заработок МР по сравнению с линейной после ${formatPostBreak(DC.C10P16000D3)} ОВ.
<br>
<br>
Уровень Глифа масштабируется от комбинации Очков Вечности, Репликантов и Растянутого Времени, с минимальным уровнем в
${formatInt(1)}. Тип, эффекты, и редкость Глифа случайны (но не полностью).
<br>
<br>
Вы получаете ровно ${formatInt(1)} Очко Перка за Реальность.
<br>
<br>
<b>Горячая Клавиша: Y/Н</b> попытается совершить сброс Реальности.
`,
      isUnlocked: () => PlayerProgress.realityUnlocked() || TimeStudy.reality.isBought,
      tags: ["rm", "machines", "glyph", "perk", "reset", "prestige", "endgame", "lategame"],
      tab: "reality/upgrades"
    }, {
      name: "Glyphs",
      textName: "Глифы",
      info: () => `
Глиф - экипируемый объект, имеющий 4 параметра:
<br>
<b>Тип</b> - Название, данное Глифу, определяющее то, какую сторону игры этот Глиф улучшает
(например. "Глиф X"). Это определяет эффекты, которые Глиф может получить.
<br>
<b>Уровень</b> - Влияет на то, насколько сильным будет Глиф, зависит от того, сколько различных ресурсов вы
получили в Реальности, после которой вам достался этот Глиф.
<br>
<b>Редкость</b> - Процент между ${formatPercents(0)} и ${formatPercents(1)}, который так же влияет на силу Глифа.
Значение случайно, но зависит от различных улучшений.
Проценты - рейтинг качества, чем выше - тем лучше. Определённым значениям качества даны названия, такие как
Обычный или Редкий.
<br>
<b>Эффекты</b> - Бонусы, которые экипировка Глифа даст вам, всего может быть до четырёх (на данном этапе).
Глифы с большим уровнем или редкостью в основном будут давать больше эффектов.
<br>
<b>Примечание: Ваш первый Глиф будет иметь фиксированный эффект и редкость, но его уровень будет зависеть от прогресса,
которого вы достигли перед сбросом Реальности. Как только вы получите Глиф, его параметры не могут быть изменены.</b>
<br>
<br>
Чтобы экипировать Глиф, перетащите его иконку из инвентаря в один из кружков в центре вкладки Глифов.
Когда экипирован, иконка Глифа становится округлённой и его эффекты добавляются к списку справа.
<br>
<br>
Экипировка множества Глифов с одинаковыми эффектами совместит их эффекты; эффекты с "+" в основном складываются,
а эффекты с "×" в основном перемножаются.
<br>
<br>
Вы можете экипировать Глифы в <i>пустые</i> активные слоты в любой момент во время Реальности, что мгновенно применит
эффекты нового Глифа. Вы также можете перетащить Глифы в уже занятые слоты чтобы заменить их, но это перезапустит
текущую Реальность.
<br>
<br>
По стандарту, слоты в первых рядах вашего инвентаря являются "защищенными". Новые Глифы никогда не будут вставлены в
эти места (даже если места в вашем инвентаре не осталось), и они не зависят от Сортировки или Авто Очистки. Есл у
вас закончится место для новых Глифов, любые Глифы, которые вы получили бы, будут удаляться (или жертвоваться, если
жертвование разблокировано).
<br>
<br>
Вы можете удалить Глифы из инвентаря, если кликните на них с зажатым Шифтом. Это попросит вас подтвердить удаление.
Задержка shift и ctrl вместе при нажатии обойдёт подтверждение. <b>Однако удаление Глифов не даст вам никакого
преимущества, за исключением очистки места в инвентаре, если вы делаете это до разблокировки Жертвования Глифов!</b>
<br>
<br>
Как только вы откроете Жертвование Глифов, вы сможете отключить подтверждение удаления Глифов.
Прохождение Реальности с отключенным подтверждением выберет случайный Глиф.
`,
      isUnlocked: () => PlayerProgress.realityUnlocked() || TimeStudy.reality.isBought,
      tags: ["reality", "sacrifice", "level", "endgame", "lategame"],
      tab: "reality/glyphs"
    }, {
      name: "Perks",
      textName: "Перки",
      info: () => `
Перки - тип улучшений, открывающийся после Реальности. Каждый эффект Перка уникален, но большинство из них даёт
небольшие изменения для улучшения качества игрового процесса. Все Перки требуют только ${formatInt(1)} Очко Перка для
покупки.
<br>
<br>
Каждую Реальность вы получаете ${formatInt(1)} Очко Перка, которое может быть потрачено на улучшение в древе, начиная
с перка START. Вы можете разблокировать только те Перки, которые совмещены с ранее купленными Перками.
<br>
<br>
Перки могут бывают двух форм - круглые или ромбообразные. Единственное различие заключается в том, что ромбообразные
Перки дают Очки Автоматизатора в дополнение к своим обычным эффектам. Различные Перки также имеют различные цвета,
грубо обозначающие то, на какую часть игры влияет данный Перк.
`,
      isUnlocked: () => PlayerProgress.realityUnlocked() || TimeStudy.reality.isBought,
      tags: ["pp", "reality", "tree", "endgame", "lategame"],
      tab: "reality/perks"
    }, {
      name: "Automator Overview",
      textName: "Обзор Автоматизатора",
      info: () => `
Вы можете получить доступ к информации об Автоматизаторе, перейдя во вкладку Автоматизатора.
<br>
<br>
<b>Горачая Клавиша: U/Г</b> остановит/продолжит работу Автоматизатора.
`,
      isUnlocked: () => Player.automatorUnlocked,
      tags: ["automation", "reality", "code", "script", "endgame", "lategame"],
      tab: "automation/automator"
    }, {
      name: "Automator Technical Details",
      textName: "Технические Детали Автоматизатора",
      info: () => `
<b>Технические Ограничения</b>
<br>
<br>
Существует несколько ограничений для скриптов. Эти ограничения включают в себя:
<br>
- Индивидуальные скрипты ограничены максимумов в ${formatInt(AutomatorData.MAX_ALLOWED_SCRIPT_CHARACTERS)}
символов каждый и все скрипты вместе не должны превышать ${formatInt(AutomatorData.MAX_ALLOWED_TOTAL_CHARACTERS)}
символов всего.
<br>
- Названия скриптов не могут превышать ${formatInt(AutomatorData.MAX_ALLOWED_SCRIPT_NAME_LENGTH)} символов.
<br>
- Определённые константы не могут иметь названия длиннее, чем ${
  formatInt(AutomatorData.MAX_ALLOWED_CONSTANT_NAME_LENGTH)}
символов, или значения, длиннее чем ${formatInt(AutomatorData.MAX_ALLOWED_CONSTANT_VALUE_LENGTH)} символов.
<br>
- Вы не можете иметь более ${formatInt(AutomatorData.MAX_ALLOWED_SCRIPT_COUNT)} скриптов или
${formatInt(AutomatorData.MAX_ALLOWED_CONSTANT_COUNT)} определённых констант.
<br>
<br>
<b>Сохранение Скриптов</b>
<br>
<br>
Скрипты ввтоматически сохраняются во время редактирования, но не сохраняются вместе с вашей игрой до тех пор, пока авто
сохранение не сделает полное сохранение игры. Все изменения в скриптах, превышающие лимит символов, не будут сохранены
до тех пор, пока вы не сократите скрипты.
<br>
<br>
<b>Тики Автоматизатора</b>
<br>
<br>
"Таймер выполнения" Автоматизатора основан на реальном времени, поэтому на него не влияют игровые улучшения по типу
Чёрной Дыры. Однако это выполнение происходит независимо от тика производства игры, поэтому на высоких скоростях
Автоматизатор может просчитывать несколько тиков за один игровой тик.
<br>
<br>
Некоторые команды более интенсивны для кода игры и могут занять больше времени, чем один тик Автоматизатора на
слабых компьютерах. В этом случае Автоматизатор исполнит эти команды и попытается догнать выполнение команд, выходя
за предел своей максимальной скорости работы.
<br>
<br>
<b>Взаимодействие с Офлайн Прогрессом</b>
<br>
<br>
Во время симуляции Офлайн Прогресса игровые тики длятся гораздо больше, чем во время просчёта в реальном времени.
Это может привести к потере эффективности во время просчёта офлайн. Дополнительно, команда PAUSE может вести себя
странным образом из-за того, что она основана на реальном времени.
`,
      isUnlocked: () => Player.automatorUnlocked,
      tags: ["automation", "reality", "code", "script", "endgame", "lategame"],
      tab: "automation/automator"
    }, {
      name: "Black Hole",
      textName: "Чёрная Дыра",
      info: () => `
Чёрная Дыра - механика, которая переодически меняет скорость игры. Игра некоторое время будет работать с обычной
скоростью, а затем на протяжении короткого промежутка времени экстремально ускорится, и так по кругу.
<br>
<br>
Повышенная от Чёрной Дыры скорость игры гораздо сильнее, чем скорость тика, потому что, в отличии от скорости тика, она
влияет на <i>всё одинаково</i>, включая вещи, которые лишь частично зависят от скорости тика
(например Измерения Бесконечности/Времени), вещи, которые обычно полностью не зависят (например генерация РВ/ВТ),
и эффекты, которые увеличиваются только на проведённом времени (например множители безучастныого пути ОБ/ОВ).
<br>
<br>
Несмотря на это, некоторые функции части игры остаются неизменными.
В таких случаях, будет оказано, что эта функция зависит от <i>реального времени</i>, а не от
<i>игрового времени</i>. Один такой пример - набор Перков, автоматически проходящих Вечные Челленджи.
В остальных случаях, с этого момента, все отсылки к времени обозначают <i>игровое время</i>.
Заметьте, что это так же влючает ситуации, когда вы хотите, чтобы <i>меньшее</i> количество времени было потрачено, как,
например, для получения Улучшения Реальности "Репликативная Стремительность".
<br>
<br>
Вы можете покупать улучшения для Чёрной Дыры за Машины Реальности. Вот три улучшения для Чёрной Дыры:
<br>
<b>Интервал</b> - То, как долго Чёрная Дыра неактивна между вспышками,
уменьшается на ${formatPercents(0.2)} за улучшение.
<br>
<b>Мощность</b> - Насколько сильно ускоряется время во время вспышки,
увеличивается на ${formatPercents(0.35)} за улучшение.
<br>
<b>Время действия</b> - Длительность вспышки,
увеличивается на ${formatPercents(0.3)} за улучшение.
<br>
<br>
Спустя ${formatInt(100)} дней <i>игрового времени</i> после разблокировки Чёрной Дыры, вы разблокируете возможность
купить Улучшение Реальности, позволяющее вам получить вторую Чёрную Дыру.
Таймер Второй Чёрной дыры продвигается только тогда, когда активна первая Чёрная Дыра. Поэтому, например, если первая
Чёрная Дыра имеет время действия в ${formatInt(4)} минуты и вторая имеет интервал ${formatInt(8)} минут, вторая
Чёрная Дыра будет активироваться после двух циклов первой Чёрной Дыры, независимо от интервала первой Чёрной Дыры.
Заметьте, что таймер, показываемый в игре, учитывает это и показывает реальное время до активации второй Чёрной Дыры.
<br>
<br>
Когда Чёрная Дыра активна как минимум ${formatPercents(0.9999, 2)} времени, она становится постоянно активной.
Это отслеживается отдельно для каждой Дыры.
<br>
<br>
Когда Офлайн, циклы Чёрных Дыр будут проходить как обычно.
Офлайн время симулирует сегменты активации и дизактивации Чёрной Дыры с различной длиной тика для того чтобы уменьшить
негативные эффекты небольшого количества тиков; раздел "Офлайн Прогресс" был обновлён, загляните туда за подробностями.
<br>
<br>
Чёрная Дыра может быть остановлена. Однако, после снятия паузы, она будет 
${BlackHoles.ACCELERATION_TIME} секунд реального времени постепенно востанавливать свою скорость.
Это время всё ещё будет продвигать цикл так, как если бы Дыра была запущена на полную мощность;
<br>
<br>
Пауза и снятие паузы влияет на обе Чёрные Дыры; они не управляются по отдельности. Они могут быть автоматически
остановлены за ${BlackHoles.ACCELERATION_TIME} секунд реального времени до активации, если вы нажмёте на соответствующую
кнопку во вкладке Чёрной Дыры.
<br>
<br>
<b>Информация Цен Улучшений:</b>
<br>
<b>Интервал</b> - Базовая цена в ${formatInt(15)} МР и повышение в ${formatX(3.5, 0, 1)} за улучшение.
<br>
<b>Мощность</b> - Базовая цена в ${formatInt(20)} МР и повышение в ${formatX(2)} за улучшение.
<br>
<b>Время действия</b> - Базовая цена в ${formatInt(10)} МР и повышение в ${formatX(4)} за улучшение.
<br>
<b>Масштабирование увеличенной цены:</b> После ${format(1e30)} МР, цена множителя между покупками увеличивается на
+${format(0.2, 0, 1)} за улучшение. После ${format(Number.MAX_VALUE, 1)} МР, применяется новое масштабирование,
игнорирующее предыдущее. С этого момента, все улучшения будут вести себя так, как если бы базовая цена была
${format(DC.E310)} и увеличение было бы на ${format(1e6)}, ${format(1e7)}, и так далее
(${formatX(10)} между улучшениями).
<br>
<b>Чёрная Дыра 2:</b> Все улучшения имеют базовую цену в ${formatX(1000)} раз больше, чем у первой дыры, но такие же
множители цены.
<br>
<br>
<b>Горячая Клавиша: B/И</b> остановит/снимет паузу Чёрных Дыр.
`,
      isUnlocked: () => player.blackHole[0].unlocked,
      tags: ["reality", "time", "speed", "duration", "interval", "rm", "endgame", "lategame"],
      tab: "reality/hole"
    }, {
      name: "Celestials",
      textName: "Божества",
      info: () => `
После того, как вы получите все Улучшения Реальности, разблокируется первое Божество. Это открывает новую вкладку для
Божеств, рядом с вкладкой Реальности. Первая подвкладка во вкладке Божеств показывает карту, названную
"Навигация Божеств". Она обновляется в соответствии с вашим прогрессом. Только часть будет видна на
момент разблокировки, но новый контент постепенно будет раскрыт, обычно с визуальной демонстрацией приближения к цели.
<br>
<br>
Каждое Божество имеет уникальные механики и улучшения, и вам необходимо победить их всех, чтобы пройти игру.
Разблокировка или победа над Божеством имеет различные условия в зависимости от механик Божества.
<br>
<br>
Все Божества имеют свою собствунную Божественную Реальность.
<br>
<br>
Божества - вневременные существа. Если не сказано иначе, все новые механики, предоставляемые Божествами, не зависят от
скорости игры, и вместо этого опираются на реальное время.
`,
      isUnlocked: () => Teresa.isUnlocked,
      tags: ["reality", "challenges", "endgame", "lategame"],
      tab: "celestials/celestial-navigation"
    }, {
      name: "Teresa, Celestial of Reality",
      textName: "Тереза, Божество Реальности",
      alias: "Teresa",
      textAlias: "Тереза",
      info: () => `
Тереза - первое Божество. Разблокируется Достижением 147, которое требует получения всех Улучшений Реальности.
<br>
<br>
На главном экране есть полоса с надписью "Заправить МР". Это позволяет вам класть МР в этот контейнер
для множителя Машин Реальности. МР, которые были заправлены в этот контейнер, не могут быть получены назад.
Когда вы достигните ${format(TeresaUnlocks.run.price)} МР внутри контейнера, вы разблокируете Реальность Терезы.
<br>
<br>
Когда вы пройдёте Реальность Терезы,
${Teresa.runCompleted
    ? "множитель Жертвования Глифов будет умножен на основе количества антиматерии, заработанного за забег"
    : "<div style='color: var(--color-bad);'>(пройдите Реальность Терезы чтобы увидеть награду)</div>"}.
Прохождение Реальности Терезы - только часть истории; вам потребуется продолжать заправлять МР для того чтобы
продолжить прогресс. Когда вы достигните ${format(TeresaUnlocks.effarig.price)} МР в контейнере, вы разблокируете
следующее Божество.
<br>
<br>
${Teresa.runCompleted
    ? "Реальность Терезы может быть посещена вновь, и её награда будет сильнее, если вы " +
      "достигните большего количества антиматерии на повторном забеге."
    : "(Доступно больше информации - пройдите Реальность Терезы)"}
`,
      isUnlocked: () => Teresa.isUnlocked,
      tags: ["rm", "endgame", "lategame", "perks", "sacrifice", "boo", "ghost", "celestial"],
      tab: "celestials/teresa"
    }, {
      name: "Effarig, Celestial of Ancient Relics",
      textName: "Эффариг, Божество Древних Реликвий",
      alias: "Effarig",
      textAlias: "Эффариг",
      info: () => `
Эффариг - второе Божество, которое вы встречаете.
Разблокируется за заправку ${format(TeresaUnlocks.effarig.price)} МР в контейнер Терезы.
<br>
<br>
Эффариг предоставляет новую валюту - Осколки Реликвий. Они зарабатываются использованием различных эффектов Глифов в
Реальности. Количество различных активных Эффектов очень сильно влияет на заработок Осколков Реликвий, ОВ влияют на них
гораздо меньше. Осколки Реликвий - валюта для разблокировок Эффарига, и будут получены с каждой следующей Реальности.
<br>
<br>
Используя Осколки Реликвий, вы можете купить множество улучшений (смотри "Продвинутые Механики Глифов"), которые
улучшают ваши Глифы и позволяют вам фильтровать их на основе их эффектов и редкости, когда вы осуществляете полностью
автоматические Реальности.
<br>
<br>
Финальная разблокировка Эффарига - его Реальность, на ${format(GameDatabase.celestials.effarig.unlocks.run.cost)}
Оксолков Реликвий.
${EffarigUnlock.run.isUnlocked
    ? "Его Реальность разделена на три слоя: Бесконечность, Вечность и Реальность. Вы должны пройти каждый слой, " +
      "для того чтобы получить доступ к следующему. Прохождение Вечности Эффарига открывает следующее Божество."
    : "<div style='color: var(--color-effarig--base);'>(разблокируйте Реальность Эффарига чтобы увидеть детали)</div>"
}
<br>
<br>
Прохождение Реальности Эффарига разблокирует
${EffarigUnlock.reality.isUnlocked
    ? `новый тип Глифа, Глиф <span style='color: var(--color-effarig--base);'>Эффарига</span>. Глифы Эффарига имеют
      ${formatInt(7)} различных возможных эффектов, которые вы можете посмотреть в настройках Фильтра Глифов. Вы можете
      экипировать только один Глиф Эффарига за раз.
${Ra.unlocks.glyphEffectCount.canBeApplied
    ? `Благодаря уровню Эффарига 10 внутри Ра, ограничение на эффекты Глифа Эффарига снимается. Теперь все получаемые
        Глифы Эффарига могут иметь до ${formatInt(7)} эффектов одновременно.`
    : `Глифы Эффарига могут иметь не более ${formatInt(4)} эффектов, а эффекты множителя МР и нестабильности Глифов
        не могут вместе присутствовать на одном Глифе.`}`
    : "<span style='color: var(--color-effarig--base);'>(пройдите Реальность Эффарига чтобы увидеть детали " +
        "награды)</span>"}
<br>
<br>
`,
      isUnlocked: () => TeresaUnlocks.effarig.canBeApplied,
      tags: ["glyph", "sacrifice", "shards", "reality", "spectralflame", "lategame", "endgame", "celestial"],
      tab: "celestials/effarig"
    }, {
      name: "Advanced Glyph Mechanics",
      textName: "Продвинутые Механики Глифов",
      info: () => `
Регулировка уровня Глифов покупается за ${format(GameDatabase.celestials.effarig.unlocks.adjuster.cost)} Осколков
Реликвий. Она позволяет вам расставлять вес для каждого ресурса (ОВ, РВ, Репликанты, Вечности), определяющий то,
насколько сильно уровень Глифа зависит от каждого ресурса.
<br>
<br>
Автоматическая Фильтрация Глифов покупается за ${format(GameDatabase.celestials.effarig.unlocks.glyphFilter.cost)}
Осколков Реликвий. Эта система использует один из множества методов для присвоения счёта при выборе Глифа, и затем
выбирает Глиф с наибольшим счётом. После выбора Глифа, она сравнивает счёт с задержкой, и, либо сохраняет его, либо
Жертвует. Подробное описание режимов можно посмотреть на вкладке Глифов. Три базовых режима включают в себя:
<br>
<b>Наименьшее общее Жертвование</b>
<br>
<b>Количество эффектов</b>
<br>
<b>Режим Задержки Редкости</b> 
<br>
<br>
Дополнительно, существует ещё два продвинутых режима с дополнительной гибкостью. Изначально они могут быть бесполезны,
но возможно пригодятся позже:
<br>
<b>Режим Указанного Эффекта</b>
<br>
<b>Режим Счёта Эффектов</b>
<br>
<br>
Режим Фильтра Глифов - глобальная настройка, которая применяется ко всем типам Глифов одновременно; например, вы не
можете отфильтровать Глифы мощности с "Задержкой Редкости", а Глифы времени с "Указанным Эффектом". Выбор режима
потребует от вас настройки каждого типа Глифа внутри настроек выбранного режима фильтрации.
<br>
<br>
Пресеты Глифов покупаются за ${format(GameDatabase.celestials.effarig.unlocks.setSaves.cost)} Осколков
Реликвий. Это разблокирует ${formatInt(7)} слотов, позволяющих вам сохранять ваши текущие экипированные Глифы в эти
пресеты. Вы не можете перезависать пресет, для этого вы сначала должны удалить его. Когда вы загружаете пресет, каждый
Глиф находится и экипируется. Если какой-либо Глиф не найден, отобразится предупреждение, и оставшиеся Глифы все
равно экипируются.
`,
      isUnlocked: () => EffarigUnlock.adjuster.isUnlocked,
      tags: ["glyph", "weight", "adjustment", "sacrifice", "filter", "threshold", "set", "save", "reality", "lategame",
        "endgame"],
      tab: "celestials/glyphfilter"
    }, {
      name: "The Nameless Ones, Celestial of Time",
      textName: "Безымянные, Божество Времени",
      alias: "Nameless Ones",
      textAlias: "Безымянные",
      info: () => `
Безымянные - третье Божество, разблокируемое за прохождение Вечности Эффарига.
<br>
<br>
После разблокировке Безымянных вы сразу же получаете доступ к двум новым механикам, связанным с временем. Вы можете
накапливать "игровое время", заряжая вашу Чёрную Дыру, и можете копить "реальное время", намеренно останавливая
производство. Накопленное игровое время также используется в качестве валюты для покупки разблокировок Безымянных.
<br>
<br>
Зарядка Чёрной Дыры даёт вам накопленное игровое время, взамен устанавливая скорость вашей игры на
${formatInt(1)}. Основное применение этого времени - разрядка Чёрной Дыры, которая использует ваше игровое время для
скачка во времени вперед на время, равное накопленному игровому времени.
<br>
<br>
Накопление реального времени полностью останавливает всё производство, эффективно останавливая вашу игру. За каждую
секунду реального времени, вы получаете накопленное реальное время (модифицированное некоторым коэффициентом
эффективности). Вы можете использовать накопленное реальное время для того чтобы усилить Реальность во вкладке Глифов.
Когда вы проходите Реальность, это использует всё ваше накопленне реальное время за раз чтобы попытаться
повторить эту Реальность вновь и вновь, давая вам все те награды, которые вы получили бы с повторений. Например, если
вы имеете ${formatInt(50)} накопленных минут и усиливаете Реальность, продлившуюся ${formatInt(10)} минут, и дающую
дающую ${format(DC.E30)} МР и ${format(DC.E12)} Осколков Реликвий, усиленная Реальность даст вам ${format(5e30)} МР,
${format(5e12)} Осколков Реликвий, ${formatInt(5)} Глифов (отфильтрованных вашими настройками),
и ${formatInt(5)} Очков Перка.
<br>
<br>
Однако если ваша Реальность продлилась менее ${formatInt(1)} секунды, коэффициент усиления ограничен количеством
накопленных секунд. Например, если вы имеете ${formatInt(1000)} накопленных секунд и усиливаете Реальность, продлившуюся
${format(0.2, 2, 2)} секунд, вы используете ${formatInt(200)} секунд для симуляции ${formatInt(1000)} Реальностей.
<br>
<br>
Вы можете переключить настройку для автоматического накопления секунд реального времени в офлайне.
<br>
<br>
Их первая разблокировка стоит ${
  format(TimeSpan.fromMilliseconds(ENSLAVED_UNLOCKS.FREE_TICKSPEED_SOFTCAP.price).totalYears)}
лет накопленного игрового времени. Она повышает мягкое ограничение Улучшений Скорости Тика, зарабатываемых с
Измерений Времени (момент, при котором цена начинает повышаться быстрее)
на ${format(1e5)} Улучшений Скорости Тика.
<br>
<br>
При ${format(TimeSpan.fromMilliseconds(ENSLAVED_UNLOCKS.RUN.price).totalYears)} годах накопленного игрового времени,
вы наконец можете разблокировать их Реальность. Награда за прохождение Реальности Безымянных - 
${Enslaved.isCompleted
    ? "разблокировка Тессерактов, которые имеют свой раздел в этом меню."
    : "<span style='color: var(--color-bad);'>(пройдите Реальность Безымянных чтобы увидеть детали награды)</span>"}
<br>
<br>
Безымянные не открывают следующее Божство напрямую.
`,
      isUnlocked: () => EffarigUnlock.eternity.isUnlocked,
      tags: ["reality", "time", "blackhole", "lategame", "endgame", "testers", "celestial",
        ...credits.people.map(p => p.name)
      ],
      tab: "celestials/enslaved"
    }, {
      name: "Tesseracts",
      textName: "Тессеракты",
      info: () => `
Тессеракты - новый ресурс, который вы разблокируете за прохождение Реальности Безымянных.
<br>
<br>
Измерения Бесконечности обычно ограничены на ${format(InfinityDimensions.HARDCAP_PURCHASES)} общих покупок,
что ограничивает то, насколько их множители могут вырасти, так как со временем вы больше не можете покупать их.
Тессеракты позволяют вам увеличить это ограничение за трату Очков Бесконечности.
<br>
<br>
Цена Тессерактов увеличивается супер-экспоненциально, но каждый следующий Тессеракт значительно сильнее предыдущего.
Количество Тессерактов никогда не сбрасывается.
<br>
<br>
Вы можете увидеть дополнительную информацию о вашем текущем количестве Тессерактов и цене слудеющего во вкладке
Измерений Бесконечности. Дополнительно, текущее количество Очков Бесконечности также показывает процент до следующего
Тессеракта. Если доступен, кнопка Бесконечности визуально поменяется и перенесёт вас на вкладку Измерений Бесконечности
после нажатия.
`,
      isUnlocked: () => Enslaved.isCompleted,
      tags: ["reality", "lategame", "endgame", "tesseract", "id", "celestial"],
      tab: "celestials/tesseract"
    }, {
      name: "V, Celestial of Achievements",
      textName: "Ви, Божество Достижений",
      alias: "V",
      textAlias: "Ви",
      info: () => `
Ви - Божество, не разблокирующееся другим Божеством,
а открывающееся за получение Достижения 151 (ряд ${formatInt(15)}, столбец ${formatInt(1)},
"Тебе всё равно это было не нужно").
<br>
<br>
После того как подвкладка открыта Достижением, вы встречаетесь с набором требований для полной разблокировки Ви.
Вы должны пройти ${formatInt(GameDatabase.celestials.v.mainUnlock.realities.requirement)} Реальностей и иметь
${format(GameDatabase.celestials.v.mainUnlock.realityMachines.requirement)} неиспользованных МР.
Дополнительно, вам необходимо достичь ${format(GameDatabase.celestials.v.mainUnlock.eternities.requirement)} Вечностей,
${format(GameDatabase.celestials.v.mainUnlock.infinities.requirement)} Бесконечностей,
${format(GameDatabase.celestials.v.mainUnlock.dilatedTime.requirement)} Растянутого Времени и
${format(GameDatabase.celestials.v.mainUnlock.replicanti.requirement)} Репликантов, всё это в одной Реальности.
<br>
<br>
Когда вы удовлетворяете все эти условия, вам будет доступна Реальность Ви.
${VUnlocks.vAchievementUnlock.isUnlocked
    ? `Прохождение Реальности само по себе - только начало.Ви имеет шсть различных требований, каждое из которых
      требует от вас определённого прогресса в Реальности Ви. Прохождение требования награждает вас Достижением Ви.
      Достижения Ви постоянны и остаются после выхода из Реальности Ви. Их не обязательно получать одновременно.
      <br>
      <br>
      После удовлетворения требования, порог Достижения Ви повышается, и оно может быть пройдено вновь, если вы
      достигните следующей цели.  Вы можете пройти каждую категорию Достижений Ви до шести раз.
      Пройденные Достижения Ви дают две вещи:
      <br>
      - После достижения определённого общего количества Достижений Ви, вы автоматически разблокируете улучшения на
        вкладке Ви без необходимости тратить какие-либо ресурсы.
      <br>
      - Каждое Достижение Ви также даёт одну Космическую Теорему.
      <br>
      <br>
      Уменьшение цели разблокируется за ${formatInt(2)} Достижения Ви и позволяет вам уменьшить некоторые требования
      Достижений Ви за Очки Перков. Цена уменьшения цели не увеличивается после использования, и также уменьшит
      будущие уровни.
      <br>
      <br>
      Космические Теоремы позволяют вам покупать Временные Исследования, которые обычно заблокированы, такие как
      множество путей на Развилке Темпа, или оба Временных Исследования в парах тьмы/света, ближе к концу.
      Как Временные Теоремы, они свободно возвращаются назад, если вы осуществляете сброс исследований.
      С достаточным количеством Космических Теорем вы в конце концов сможете купить каждое доступное Исследование
      одновременно!
      <br>
      <br>
      Достигнув ${formatInt(36)} Достижений Ви (и тем самым пройдя все Достижения Ви), вы откроете следующее Божество.`
    : "<span style='color: var(--color-bad);'>(разблокируйте Реальности Ви чтобы увидеть детали)</span>"}
`,
      isUnlocked: () => Achievement(151).isUnlocked,
      tags: ["reality", "lategame", "endgame", "girlfriend", "challenges", "achievement", "space", "theorems",
        "study", "triad", "celestial"],
      tab: "celestials/v"
    }, {
      name: "Ra, Celestial of the Forgotten",
      textName: "Ra, Божество Забытого",
      alias: "Ra",
      textAlias: "Ра",
      info: () => `
Ра - пятое Божество, разблокируемое за полное прохождение всех Достижений Ви. Оно использует воспоминания, для того
чтобы вернуть и усилить позитивные эффекты предыдущих Божеств. Со временем, вы разблокируете четырёх предыдущих Божеств
<i>внутри</i> Ра, где каждое Божество будет давать дополнительные улучшения, относящиеся к их оригинальной теме.
<br>
<br>
Каждое предыдущее Божество внутри Ра получает уровни используя воспоминания, которые пассивно со временем генерируются
от Кусков Воспоминаний. Куски Воспоминаний могут быть заработаны только внутри Реальности Ра, но снаружи Реальности
Куски будут пассивно сгенерированы на основе некоторых ресурсов. Если вы накапливаете реальное время, вы не получите
никаких Кусков Внутри Реальности Ра, но Воспоминания будут генерироваться как обычно. Наличие
${formatInt(Ra.remembrance.requiredLevels)} уровней среди всех Божеств разблокирует Вспоминаемость,
которая позволяет вам выбрать определённое Божество и дать ему бонус к заработку Кусков внутри Реальности Ра.
<br>
<br>
Воспоминания могут быть потрачены на три вещи - повышение заработка Кусков Воспоминаний, повышение заработка
Воспоминаний и повышение уровня Божества. Когда вы начинаете Ра, только Тереза разблокирована, и каждое следующее
Божество разблокируется достижением уровня ${formatInt(8)} для предыдущего Божества. Уровни ограничены на
${formatInt(25)}.
<br>
<br>
Тереза разблокирует возможность заряжать Улучшения Бесконечности, делая их сильнее. Также Тереза
улучшает эффекты Глифов, как только вы достигните определённых порогов в Жертвовании Глифов.
<br>
<br>
На уровне ${formatInt(2)}, Эффариг Разблокирует
${Ra.unlocks.effarigUnlock.canBeApplied
    ? "новую механику, Алхимию Глифов, и затем также усиливает Глифы Эффарига, постепенно избавляясь почти от всех " +
      "случайных элементов во время генерации Глифов. Алхимия Глифов имеет свой собственный раздел в этом меню."
    : "<span style='color: var(--color-bad);'>(разблокируйте Эффарига внутри Ра, чтобы посмотреть детали)</span>"}
<br>
<br>
Безымянные разблокируют
${Ra.unlocks.enslavedUnlock.canBeApplied
    ? "дополнительные механики, связанные с зарядкой Чёрных Дыр, а также значительно усиливают их."
    : "<span style='color: var(--color-bad);'>(разблокируйте Безымянных внутри Ра, чтобы посмотреть детали)</span>"}
<br>
<br>
Ви разблокирует
${Ra.unlocks.vUnlock.canBeApplied
    ? "Исследования Триад, которые являются новыми исследованиями, и расположены ближе к концу Древа. Они покупаются " +
      "за Космические Теоремы. Каждое Исследование Триад требует от вас наличие трёх рядом находящихся исследований, " +
      "для того чтобы вы смогли его купить. Ви также разблокирует меньший набор" +
      "ещё более сложных Достижений Ви, которые можно пройти для получения дополнительных Космических Теорем."
    : "<span style='color: var(--color-bad);'>(разблокируйте Ви внутри Ра, чтобы посмотреть детали)</span>"}
<br>
<br>
Ра не разблокирует следующее Божество напрямую.`,
      isUnlocked: () => VUnlocks.raUnlock.isUnlocked,
      tags: ["reality", "memories", "razenpok", "levels", "glyphs", "lategame", "endgame",
        "effarig", "teresa", "nameless", "v", "celestial"],
      tab: "celestials/ra"
    }, {
      name: "Glyph Alchemy Resources",
      textName: "Ресурсы Алхимии Глифов",
      info: () => `
Алхимия Глифов - механика, разблокируемая достижением ${formatInt(2)} уровня Эффарига внутри Ра. Она разблокирует
возможность Переработать ваши Глифы в Алхимические Ресурсы, ассоциируемые с каждым типом Глифа. Вы можете Переработать
Глифы, выставив соответствующую настройку на вкладке Глифов, в подменю Тип Жертвования.
Каждый Алхимический Ресурс имеет уникальный эффект, который вы можете посмотреть на вкладке Алхимии.
<br>
<br>
В дополнение ко всем остальным параметрам, Глифы теперь имеют <i>ценность переработки</i>, определяющую то, как
много ресурсов вы получите за Переработку данного Глифа. Это значение основывается на кубе уровня Глифа, изменённого
так, что Глиф уровня ${formatInt(10000)} соответствует ${formatInt(10000)} Алхимического Ресурса. Один Глиф, однако,
даёт только ${formatPercents(GlyphSacrificeHandler.glyphRefinementEfficiency)} его значения, когда переработан.
Это значения для Глифов ${formatPercents(1)} редкости; Глифы меньшей редкости всё ещё имеют такое же ограничение, но
дают пропорционально меньше ресурсов. Например, Глиф ${formatPercents(0.5)} редкости даст только половину.
<br>
<br>
Алхимические Ресурсы не могут быть получены бесконечно; для каждого ресурса существует ограничение, которое основано
на наибольшей ценности переработки всех Глифов этого типа, которые вы переработали. Например, если наибольший
уровень Глифа Времени, который вы переработали, был ${formatInt(8000)} (ценность переработки:
${formatInt(GlyphSacrificeHandler.levelRefinementValue(8000))}), тогда независимо от того, как много Глифов Времени
вы переработаете, вы никогда не получите более чем ${formatInt(GlyphSacrificeHandler.levelRefinementValue(8000))}
ресурса Времени до тех пор, пока не переработаете Глиф Времени с большей ценностью переработки.
`,
      isUnlocked: () => Ra.unlocks.unlockGlyphAlchemy.canBeApplied,
      // Oh god I'm so sorry this is so many words
      tags: ["reality", "lategame", "endgame", "ra", "effarig", "alchemy", "power", "infinity", "time", "replication",
        "dilation", "cardinality", "eternity", "dimensionality", "inflation", "alternation", "synergism", "momentum",
        "decoherence", "force", "exponential", "uncountability", "boundless", "unpredictability", "multiversal",
        "reaction"],
      tab: "reality/alchemy"
    }, {
      name: "Glyph Alchemy Reactions",
      textName: "Реакции Алхимии Глифов",
      info: () => `
Алхимические Ресурсы могут быть соединены в определённые комбинации, для того чтобы создать новый ресурсы, которые
разбликируется на определённых уровнях Эффарига. Ресурсы соединяются после Реальности, независимо от усиления реальным
временем. Реакции имеют более высокую отдачу и поэтому происходят быстрее, когда значения реагентов выше. Ограничение
для ресурсов соединений равняются наименьшему ограничению реагентов. Для того, чтобы произошла реакция, текущее
количество всех реагентов должно быть больше, чем текущее количество произведённого ресурса.
<br>
<br>
Скорость реакции пропорциональна количеству используемых для реакции реагентов, но задействуется только количество
реагентов после количества продукта. Например, если вы имеете ${formatInt(10000)} всех реагентов и
${formatInt(7500)} продукта, только ${formatInt(2500)} от реагентов используется для рассчёта скорости реакции.
Если бы вы имели ${formatInt(0)} продукта, <i>все</i> и доступные реагенты, реакция прошла бы в ${formatX(4)} раза
быстрее. Наконец, если бы вы имели ${formatInt(10000)} продукта, никакая реакция не произошла бы вовсе.
<br>
<br>
Чтобы активировать или деактивировать реакцию, нажмите на круг, соответствующий продукту этой реакции. Когда реакция
может быть применена, движение линий будет показано для всех реагинтов к продукту. Если соединение - цельная линия,
это означает, что реакция не может идти из-за того, что отсутствует достаточное количество реагентов.
`,
      isUnlocked: () => Ra.unlocks.unlockGlyphAlchemy.canBeApplied,
      tags: ["reality", "lategame", "endgame", "ra", "effarig", "alchemy", "power", "infinity", "time", "replication",
        "dilation", "cardinality", "eternity", "dimensionality", "inflation", "alternation", "synergism", "momentum",
        "decoherence", "force", "exponential", "uncountability", "boundless", "unpredictability", "multiversal",
        "reaction"],
      tab: "reality/alchemy"
    }, {
      name: "Imaginary Machines",
      textName: "Мнимые Машины",
      info: () => `
Как только вы можете заработать ${format(MachineHandler.baseRMCap)} Машин Реальности за одну Реальность, вы
разблокируете возможность зарабатывать новый ресурс - Мнимые Машины. Машины Реальности будут ограничены на
${format(MachineHandler.baseRMCap)}; вы не сможете заработать больше этого лимита.
<br>
<br>
Дополнительно, вы разблокируете вкладку Мнимых Улучшений, содержащую набор улучшений, похожих на Улучшения Реальности -
каждое улучшение имеет условие, которое вам необходимо выполнить для того, чтобы разблокировать улучшение, а также
цену Мнимых Машин, которую вам необходимо заплатить, чтобы получить улучшение.
Первые два ряда улучшений могут быть куплены многократно, остальные улучшения одноразовые.
<br>
<br>
Ваше количество ММ зависит от двух вещей:
<br>
<b>Ограничение ММ</b> - Существует максимальное количество ММ, которое вы можете иметь, основанное на наибольшем
количестве МР, которое вы бы получили, если бы не было ограничения МР. Оно обновляется постоянно и поэтому будет
мгновенно увеличено, если вы преодолеете предыдущее неограниченное количество МР.
<br>
<b>Текущие ММ</b> - Со временем ваши текущие ММ будут пассивно приближаться к ограничению ММ, способом, который
экспоненциально замедляется по мере приближения. По стандарту, ММ замедляются со скоростью, при которой
<i>отсутствующее</i> (т.е. ваше ограничение минус текущее количество) поделено на два каждую минуту. Эта скорость
роста не зависит от модификаторов игрового времени.
<br>
<br>
Улучшения Мнимых Машин разблокируют последних двух Божеств.
`,
      isUnlocked: () => MachineHandler.isIMUnlocked,
      tags: ["imaginary", "machines", "reality", "lategame", "endgame"],
      tab: "reality/imag_upgrades"
    }, {
      name: "Lai'tela, Celestial of Dimensions",
      textName: "Лай'тела, Божество Измерений",
      alias: "Lai'tela",
      textAlias: "Лай'тела",
      info: () => `
Лай'тела - шестое Божество, разблокируемой покупкоу соответствующего Мнимого Улучшения за
${format(ImaginaryUpgrade(15).cost)} ММ.
<br>
<br>
Лай'тела даёт новую валюту - Тёмную Материю, предоставляющую множитель к эффекту Континуума, основанный
на наибольшем количестве Тёмной Материи, которое у вас когда-либо было. Тёмная Материя производится Измерениями
Тёмной Материи, таким же образом, как все остальные измерения в игре. В отличии от других измерений,
существует всего четыре Измерения Тёмной Материи, вместо привычных восьми. Вы начинаете с первым измерением
разблокированным, более высокие разблокируются через Мнимые Улучшения. При открытии измерений, вам даётся
${formatInt(1)} этого измерения и вы не можете заработать больше никаким образом, кроме как получением
слудеющего измерения, которое производит данное.
<br>
<br>
Каждоё Измерение Тёмной Материи, после опрделённого промежутка времени, генерирует две вещи: Тёмную Материю или
нижнее Измерений Тёмной Материи и другой ресурс - Тёмную Энергию. Производство Тёмной Материи и Измерений
Тёмной Материи за интервал равно продукту множителя Тёмной Материи и количеству измерений, которые у вас есть, а
производство Тёмной Энергии не зависит от количества измерений. Тёмная Энергия используется для производства
Сингулярностей, которые имеют собственный раздел в этом меню.
<br>
<br>
Интервалы Измерений Тёмной Материи могут быть уменьшены до минимума в ${formatInt(10)}мс.
Вы можете выбрать возвышение Измерения Тёмной Материи после достижения этой точки, которое
умножит заработок Тёмной Материи на ${formatInt(POWER_DM_PER_ASCENSION)} и Тёмной Энергии на
${formatInt(POWER_DE_PER_ASCENSION)}. Интервал умножается на ${formatInt(1200)}, но вновь может быть улучшен.
Достижение ${formatInt(10)}мс вновь позволяет вам снова возвыситься, если вы захахахохохохоХАХАХОХОХО.
<br>
<br>
Мнимое Улучшение позволяет вам разблокировать Аннигиляцию. Аннигиляция сбрасывает вашу Тёмную Материю и
Измерения Тёмной Материи, но добовляет постоянный множитель к Тёмной Материи, который применяется ко всем Измерениям
Тёмной Материи. Вы можете Аннагилировать множество раз; дополнения к множителю совмещаются аддитивно, и нет нужды
в Аннигиляции большего количества каждый раз. Вы должны иметь как минимум
${format(Laitela.annihilationDMRequirement)} Тёмной Материи для того чтобы Аннигилировать.
<br>
<br>
Лай'тела имеет Реальность, которая даёт множитель к степени Тёмной Материи Измерений Тёмной Материи, основываясь на том,
как хорошо вы прошли Реальность. Каждый раз, когда вы проходите эту Реальность менее чем за ${formatInt(30)} секунд,
ваше наибольшее доступное Измерение будет навсегда отключено при следующей попытке данной Реальности. Отключение всех
ваших измерений за прохождение этой Реальности за ${formatInt(30)} секунд восемь раз также даст вам ${formatX(8)}
множитель к заработку Тёмной Энергии.
<br>
<br>
Лай'тела не открывает следующее Божество напрямую.
`,
      isUnlocked: () => Laitela.isUnlocked,
      tags: ["omsi", "reality", "dark", "matter", "dimensions", "lategame", "endgame", "ascend", "celestial"],
      tab: "celestials/laitela"
    }, {
      name: "Continuum",
      textName: "Континуум",
      info: () => `
Когда вы разблокируете Лай'тела, ваши Измерения Антиматерии и Улучшения Скорости Тика переключаются в новый режим
производства - Континуум. Он даёт те же эффекты, как и раньше, но позволяет покупать частичные измерения или
Улучшения Скорости Тика. Эти раздробленные покупки даются бесплатно без использования антиматерии и будут
предлагать соответствующую порцию их множителя.
<br>
<br>
Кнопки покупки для Измерений Антиматерии и Улучшений Скорости Тика станут модифицированными и будут отображать
количество улучшений, коротое вы бы могли купить, если бы Континуум был неактивен, а количество покупок масштабировалось
бы мягко с антиматерией. Например, наличие ${format(2e7)} антиматерии даст вам значение Континуума в
${format(5.3, 0, 1)} для Улучшений Скорости Тика
(базовое значение в ${format(1e3)} и повышение в ${formatX(10)}), так как вы можете купить его ${formatInt(5)} и
примерно ${formatPercents(0.3)} от следующего. Континуум Скорости Тика в данном случае ускорит производство
на (множитель улучшения)<sup>${format(5.3, 0, 1)}</sup>.
<br>
<br>
Некоторые улучшения будут умножать значение Континуума напрямую, что даёт бонус к производству без влияния на
масштабирование цены. Однако эти улучшения не будут работать, если Континуум отключен на вкладке АвтоПокупщиков, что
может привести к потере производства при его отключении. Континуум делает ваши АвтоПокупщики для Измерений Антиматерии
и Скорости Тика бесполезными, поэтому, пока Континуум активен, все соответствующие настройки АвтоПокупщиков для этих
АвтоПокупщиков будут скрыты.
`,
      // Apparently continuumUnlocked is really important in a lot of places and if we keep it unlocked
      // Things break, so we check for the iMU instead.
      isUnlocked: () => ImaginaryUpgrade(15).isBought,
      tags: ["continuum", "purchase", "reality", "lategame", "endgame"],
      tab: ""
    }, {
      name: "Singularities",
      textName: "Сингулярности",
      info: () => `
Сингулярности - новый ресурс, который вы можете получить благодаря Лай'теле..
<br>
<br>
Для того чтобы получить Сингулярности, вам необходимо достигнуть ${format(200)} Тёмной Энергии. После достижения,
вы получите возможность уплотнить всю вашу Тёмную Энергию в Сингулярность, сбрасывая энергию до нуля. Любая лишняя
Тёмная Энергия выше этого значения не будет перенесена, и, поэтому, потратится впустую. Заметьте, что сбрасывается
только Тёмная Энергия, Тёмная Материя и её измерения остаются такими же, какими были.
<br>
<br>
После достижения ${formatInt(10)} Сингулярностей, вы свободно сможете увеличивать или уменьшать требование
Тёмной Энергии для уплотнения Сингулярностей на коэффициент в ${formatInt(10)} (с минимумом в ${format(200)}).
Это увеличивает или уменьшает количество Сингулярностей, зарабатываемых после сбрасывания на ограничении,
на коэффициент, <i>больше чем</i> ${formatInt(10)}, делая бо́льшие ограничения более ценными, если вы готовы ждать.
<br>
<br>
Сингулярности нужно для того, чтобы открывать Рубежи Сингулярностей, которые действуют похожим на Рубежи Вечности
образом. Разблокировка этих рубежей требует от вас достижения общего указанного количества Сингулярностей;
Сингулярности не расходуются. Существует три типа рубежей - одноразовые рубежи, многоразовые рубежи с
ограниченным количеством раз, и многоразовые рубежи, которые могут быть повторены бесконечно.
<br>
<br>
Независимо от типа рубежа, рубежи также имеют иконку, показывающую тип улучшения, которое они дают:
<br>
<b>ᛝ</b> Эти рубежи помогают в механиках, специфичных для Лай'телы
<br>
<i class="fas fa-arrows-alt"></i> Эти рубежи позволяют ресурсу в Лай'теле влиять на остальную игру
<br>
<i class="fas fa-compress-arrows-alt"></i> Эти рубежи улучшают Лай'телу на основе чего-то снаружи Лай'телы
`,
      isUnlocked: () => Laitela.isUnlocked,
      tags: ["reality", "lategame", "endgame", "laitela", "dark"],
      tab: ""
    }, {
      name: "Pelle, Celestial of Antimatter",
      textName: "Пелле, Божество Антиматерии",
      alias: "Pelle",
      textAlias: "Пелле",
      info: () => `
Когда вы покупаете последнее Мнимое Улучшение и разблокируете Пелле, вы разблокируете его вкладку, где вы можете
найти кнопку "Обречь вашу Реальность". Для того чтобы Обречь Реальность, вам необходимо получить все
${formatInt(17)} рядов Достижений, доступных на данный момент, и получить по ${formatInt(25000)} каждого
Алхимического Ресурса.
<br>
<br>
${Pelle.isDoomed
    ? `Обречение вашей Реальности начнёт новую <b>Обречённую Реальность</b>, сбрасывая игру почти полностью до
      Реальности, не давая взамен никаких наград за прогресс в текущей Реальности.
      <br>
      <br>
      Когда вы входите в Обречённую Реальность, вы сохраняете все значение в разделах Основное и Реальность во вкладке
      Статистики и все ваши рекорды Челленджей. Внутри Обречённых Реальностей, множественные улучшения, Временны́е
      Исследования, Челленджи и награды Божеств, Перки и другие игровые механики отключены и не дают наград.
      Вы можете посмотреть "Показать эффекты в Обречённой Реальности" во вкладке Пелле для больших подробностей.
      <br>
      <br>
      Остатки - новая валюта, зарабатываемая на сбросах Армагеддона. Заработок Остатков основан на вашем лучшем
      количестве за всё время антиматерии, Очках Бесконечности и Очках Вечности среи всех Обречённых Реальностей.
      Остатки производят Осколки Реликвий, которые могут быть потрачены на Улучшения Пелле.
      <br>
      <br>
      Улучшения Пелле могут быть поделены на две категории. Пять улучшений в первом ряду могут быть куплены
      многократно, но однажды достигнут ограничения. Они дают бонусы к различным аспектам игры, делая прогрессию
      внутри Обречённых Реальностей легче.
      <br>
      <br>
      Другие улучшения в нижнем ряду предлагают автоматизацию и улучшения качества жизни. Всё разблокированное
      из этих улучшений не может быть разблокировано обычными методами игры; например, никакие из Нормальных Челленджей
      не разблокируют АвтоПокупщики после прохождения, так как они заблокированы за Улучшениями Пелле.
      Вы можете использовать кнопку-переключатель над улучшениями, чтобы спрятать уже купленные улучшения, или
      кликнуть <i class="fas fa-compress-arrows-alt"></i>-иконку, чтобы скрыть и спрятать всю панель.
      <br>
      <br>
      <b>Горячая Клавиша: Z/Я</b> попытается сделать сброс Армагеддоном.`
    : "<span style='color: var(--color-bad);'><b>Вы должны Обречь вашу Реальность чтобы прочитать оставшуюся часть " +
      "текста.</b></span>"
}
`,
      isUnlocked: () => Pelle.isUnlocked,
      tags: ["reality", "antimatter", "lategame", "endgame", "final", "hevipelle", "celestial", "doom"],
      tab: "celestials/pelle"
    }, {
      name: "Pelle Strikes",
      textName: "Удары Пелле",
      info: () => `
Удары Пелле встречаются на различных событиях в Обречённой Реальности. Вы встретите первый Удар Пелле после
достижения Бесконечности в первый раз внутри Обречённой Реальности. Каждый Удар Пелле даёт ослабление к
определённому аспекту игры, который может быть просмотрен нажатием по названию Удара.
Каждый Удар Пелле также разблокирует полосу Разлома.
<br>
<br>
Полосы Разлома могут быть заполнены переключением между "Бездействует" и "Заполняется", но только два Разлома могут
"Заполняться" одновременн. Когда активен, Разлом потребляет ${formatInt(3)}%  специфичного для Разлома ресурса в
секунду. Каждый Разлом предлагает специфичный для данного Разлома эффект, основанный на общем заполненном количестве.
${PelleStrikes.eternity.hasStrike
    ? `Исключением является Распад/Коллапс/Смятение, так как его эффект ограничивается, как только вы израсходовали
    ${formatPostBreak(DC.E2000)} Репликантов всего.`
    : ""}
В дополнение, каждый Разлом предлагает три рубежа с наградами при достижении определённого количества процентов.
`,
      isUnlocked: () => PelleStrikes.infinity.hasStrike,
      tags: ["reality", "antimatter", "lategame", "endgame", "final", "pelle", "strike", "rift", "celestial"],
      tab: "celestials/pelle"
    }, {
      name: "The Galaxy Generator",
      textName: "Генератор Галактик",
      info: () => `
Когда вы достигаете ${formatInt(100)}% Рекурсии/Дисперсии/Разрушения, вы открываете <b>Генератор Галактик</b>, который
может пассивно генерировать Галактики. Генерируемые Галактики работают так же, как Галактики Антиматерии, но они
не повышают цену следующей Галактики Антиматерии. Вы также разблокируете пять новых улучшений. Первое улучшение
увеличивает базовое количество генерируемых Галактик. Остальные четыре улучшения дают множитель к этому базовому
количеству. Первые два улучшения покупаются за антиматерию и Галактики Антиматерии. Репликанты или Галактики Тахионов не
могут быть использованы для покупки этих улучшений.
<br>
<br>
<b>Генератор Галактик</b> имеет максимальное количество Галактик, которые он может генерировать. Это значение может
быть увеличено путём истощения Разломов, если оно было достигнуто.`,
      isUnlocked: () => Pelle.hasGalaxyGenerator,
      tags: ["reality", "antimatter", "lategame", "endgame", "final", "pelle", "galaxy",
        "galaxies", "generator", "celestial"],
      tab: "celestials/pelle"
    }
  ]
};

(function() {
  for (let i = 0; i < h2p.tabs.length; i++) {
    const tab = h2p.tabs[i];
    tab.id = i;
    if (tab.alias === undefined) tab.alias = tab.name;

    tab.searchTermsRelevance = {};
  }

  const searchIndex = {};

  const addTerm = (term, tab) => {
    let entry = searchIndex[term];
    if (entry === undefined) {
      entry = [];
      searchIndex[term] = entry;
    }
    if (entry.includes(tab)) return;
    entry.push(tab);
  };

  const addWord = (word, tab) => {
    const lowerCase = word.toLowerCase();
    for (let i = 0; i < lowerCase.length; i++) {
      const term = lowerCase.slice(0, i + 1);
      addTerm(term, tab);
      if (tab.searchTermsRelevance[term] === undefined) {
        tab.searchTermsRelevance[term] = ((i + 1) / lowerCase.length) ** 0.65;
      } else {
        tab.searchTermsRelevance[term] = Math.max(tab.searchTermsRelevance[term], ((i + 1) / lowerCase.length) ** 0.65);
      }
    }
  };

  const addPhrase = (phrase, tab) => {
    addWord(phrase, tab);
    for (const part of phrase.split(" ")) {
      addWord(part, tab);
    }
  };

  for (const tab of h2p.tabs) {
    addPhrase(tab.name, tab);
  }
  for (const tab of h2p.tabs) {
    for (const tag of tab.tags) {
      addPhrase(tag, tab);
    }
  }
  for (const tab of h2p.tabs) {
    addPhrase(tab.alias, tab);
  }

  const map2dToObject = function(arr, keyFun, valueFun) {
    const out = {};
    for (let idx1 = 0; idx1 < arr.length; idx1++) {
      for (let idx2 = 0; idx2 < arr[idx1].length; idx2++) {
        out[keyFun(arr[idx1][idx2], idx1, idx2)] = valueFun(arr[idx1][idx2], idx1, idx2);
      }
    }
    return out;
  };

  // Very suboptimal code coming up. If anybody has a better solution, PLEASE, implement it.
  const keyboardify = keybrd => map2dToObject(keybrd.split(",").map(str => str.split("")),
    key => key, (_key, x, y) => ({ x, y }));

  const qwerty = keyboardify(`1234567890,qwertyuiop,asdfghjkl,zxcvbnm`);
  const qwertz = keyboardify(`1234567890,qwertzuiop,asdfghjkl,yxcvbnm`);
  const azerty = keyboardify(`1234567890,azertyuiop,qsdfghjklm,wxcvbn`);
  const dvorak = keyboardify(`1234567890,'<>pyfgcrl,aoeuidhtns,;qjkxbmwvz`);
  const colemak = keyboardify(`1234567890,qwfpgjluy,arstdhneio,zxcvbkm`);
  const workman = keyboardify(`1234567890,qdrwbjfup,ashtgyneoi,zxmcvkl`);
  const qwprf = keyboardify(`1234567890,qwprfyukl,asdtghnioe,zxcvbjm`);

  const keyboards = [qwerty, qwertz, azerty, dvorak, colemak, workman, qwprf];

  const keyboardDist = function(a, b, keyboard) {
    const aPos = keyboard[a], bPos = keyboard[b];
    if (!aPos || !bPos) return 100;
    return Math.max(Math.abs(aPos.x - bPos.x), Math.abs(aPos.y - bPos.y));
  };

  // I copied this code based on OSA distance off wikipedia, with a few added changes.
  // The cost for "substitution" (third item of the first Math.min) is replaced from a static value
  // to a function which roughly estimates how likely the user is to mispress the key based on its
  // minimum distance from several common keyboard layouts.
  // I have no idea how the actual "distance" calculation works but as long as it does don't touch it.
  const howBadlyTypoedWithKeyboard = function(a, b, keyboard) {
    // If they're the same, skip all calculations
    if (a === b) return 0;
    const aLen = a.length;
    const bLen = b.length;
    // If they're way too different, don't bother
    if (Math.abs(aLen - bLen) > 3) return 100;
    // 2d Array with dimensions aLen + 1 x bLen + 1
    const d = new Array(aLen + 1).fill(0).map(() => new Array(bLen + 1).fill(0));

    for (let i = 0; i <= aLen; i++) {
      d[i][0] = i;
    }
    for (let i = 0; i <= bLen; i++) {
      d[0][i] = i;
    }

    for (let i = 1; i <= aLen; i++) {
      for (let j = 1; j <= bLen; j++) {
        const distance = keyboardDist(a[i - 1], b[j - 1], keyboard);
        const cost = distance === 0 ? 0 : 0.3 + distance * distance * 0.25;
        d[i][j] = Math.min(
          d[i - 1][j] + 0.55,
          d[i][j - 1] + 0.7,
          d[i - 1][j - 1] + cost
        );
      }
    }
    return d[aLen][bLen];
  };

  const howBadlyTypoed = function(a, b) {
    // Arbitrarily large number
    let minTypoed = 1e10;
    for (const keyboard of keyboards) {
      minTypoed = Math.min(minTypoed, howBadlyTypoedWithKeyboard(a, b, keyboard));
    }
    return minTypoed;
  };

  const specialChars = ["'", "\"", ",", "-", ".", "_"];

  const replaceSpecialChars = function(str) {
    let result = str;
    for (const i of specialChars) {
      result = result.replaceAll(i, "");
    }
    return result;
  };

  // There are a LOT of magic numbers in this code, mostly from arbitrary choices for "What number is large enough to
  // act as a placeholder for 'basically not found'?"
  // This will need some cleanup if possible.
  h2p.search = query => {
    const truncatedQuery = replaceSpecialChars(query);
    if (truncatedQuery === "") return h2p.tabs.map(x => ({ tab: x, relevance: 1.5 }));
    const searchTerms = truncatedQuery.toLowerCase().split(" ").filter(str => str !== "");

    // A higher "Relevance" value actually means it's further away from the search, important to keep in mind
    const relevances = Array.repeat(1e4, h2p.tabs.length);
    for (const searchWord of searchTerms) {
      const minimumRequirement = Math.min(searchWord.length - 0.9, 3) * 0.5;
      for (const searchIndexStr in searchIndex) {
        const typoThreshold = howBadlyTypoed(replaceSpecialChars(searchIndexStr), searchWord);
        if (typoThreshold < minimumRequirement) {
          for (const tab of searchIndex[searchIndexStr]) {
            const maxRelevance = tab.searchTermsRelevance[searchIndexStr];
            const decrease = Math.max(maxRelevance * 1.6 - 0.9, 0);
            relevances[tab.id] = Math.min(relevances[tab.id], Math.max(typoThreshold, 1 - maxRelevance) - decrease);
          }
        }
      }
    }
    const results = h2p.tabs.filter(x => relevances[x.id] < 0.9)
      .map(x => ({ tab: x, relevance: relevances[x.id] }));
    // Provide both the relevance and the tab itself

    // Sort by id first, then push more relevant results to top.
    results.sort((a, b) => a.tab.id - b.tab.id).sort((a, b) => a.relevance - b.relevance);
    // Provide both the relevance and the tab itself
    return results;
  };
}());
